import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Switch,
  ListItemAvatar,
  Avatar,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Autocomplete,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Campaign as CampaignIcon,
  HelpOutline as HelpOutlineIcon,
  PhotoCamera as PhotoCameraIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import axios from "axios";
import ImageUploadService from "../services/imageUpload";
import SafeAvatar from "../components/SafeAvatar";
import FlagIcon from "../components/FlagIcon";
import GroupDrawShow from "../components/GroupDrawShow";
import NationsLeagueMatchDays from "../components/NationsLeagueMatchDays";

import {
  NationsLeagueTeam,
  NationsLeagueMatch,
  NationsLeagueStandings,
  NationsLeagueStage,
  NationsLeaguePlayer,
  NationsLeagueFaqItem,
  Player,
} from "../types";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const NationsLeague: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { nationsLeagueId } = useParams<{ nationsLeagueId: string }>();
  const [currentTab, setCurrentTab] = useState(0);
  const [teams, setTeams] = useState<NationsLeagueTeam[]>([]);
  const hasGroupC = teams.some((t) => t.group === "C");
  const koBase = hasGroupC ? 3 : 2; // first knockout tab index (Semifinals)
  const [matches, setMatches] = useState<NationsLeagueMatch[]>([]);
  const [standingsA, setStandingsA] = useState<NationsLeagueStandings[]>([]);
  const [standingsB, setStandingsB] = useState<NationsLeagueStandings[]>([]);
  const [standingsC, setStandingsC] = useState<NationsLeagueStandings[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLocked, setIsLocked] = useState(false);

  // Announcement state
  const [announcementMessage, setAnnouncementMessage] = useState("");
  const [announcementEnabled, setAnnouncementEnabled] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);

  // Edition info (description + FAQ) state
  const [infoDescription, setInfoDescription] = useState("");
  const [infoFaq, setInfoFaq] = useState<NationsLeagueFaqItem[]>([]);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  // Group draw state (teams are created without groups). Flow: edit nations -> pots -> animated draw
  type DrawTeam = { id: string; name: string; flag: string; isBye: boolean };
  const [drawDialogOpen, setDrawDialogOpen] = useState(false);
  const [drawStep, setDrawStep] = useState<"pots" | "animation">("pots");
  const [drawing, setDrawing] = useState(false);
  const [addNationOpen, setAddNationOpen] = useState(false);
  const [teamFormName, setTeamFormName] = useState(""); // name/flag fields of the add-nation and nation dialogs
  const [teamFormFlag, setTeamFormFlag] = useState("");
  const [teamBusy, setTeamBusy] = useState(false);
  const [pots, setPots] = useState<DrawTeam[][]>([]);
  const [drawPlan, setDrawPlan] = useState<{ team: DrawTeam; groupIdx: number }[]>([]);
  const [drawRun, setDrawRun] = useState(0); // bumped per draw so the show restarts on redraw
  const [editionName, setEditionName] = useState("");
  const groupsDrawn = teams.length > 0 && teams.every((t) => !!t.group);
  const drawGroupLetters = teams.length === 12 ? ["A", "B", "C"] : ["A", "B"];
  const hasByeTeam = teams.some((t) => t.isBye);
  const teamCountValid = teams.length === 8 || teams.length === 12;

  // Knockout preview state
  const [knockoutPreviewOpen, setKnockoutPreviewOpen] = useState(false);
  const [knockoutPreview, setKnockoutPreview] = useState<any>(null);
  const [generatingKnockout, setGeneratingKnockout] = useState(false);

  // Score dialog state
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<NationsLeagueMatch | null>(
    null,
  );
  const [selectedIndividualMatch, setSelectedIndividualMatch] = useState<
    1 | 2 | 3
  >(1);

  // State for all 3 individual matches (each with 3 sets)
  const [match1Set1Team1, setMatch1Set1Team1] = useState("");
  const [match1Set1Team2, setMatch1Set1Team2] = useState("");
  const [match1Set2Team1, setMatch1Set2Team1] = useState("");
  const [match1Set2Team2, setMatch1Set2Team2] = useState("");
  const [match1Set3Team1, setMatch1Set3Team1] = useState("");
  const [match1Set3Team2, setMatch1Set3Team2] = useState("");

  const [match2Set1Team1, setMatch2Set1Team1] = useState("");
  const [match2Set1Team2, setMatch2Set1Team2] = useState("");
  const [match2Set2Team1, setMatch2Set2Team1] = useState("");
  const [match2Set2Team2, setMatch2Set2Team2] = useState("");
  const [match2Set3Team1, setMatch2Set3Team1] = useState("");
  const [match2Set3Team2, setMatch2Set3Team2] = useState("");

  const [match3Set1Team1, setMatch3Set1Team1] = useState("");
  const [match3Set1Team2, setMatch3Set1Team2] = useState("");
  const [match3Set2Team1, setMatch3Set2Team1] = useState("");
  const [match3Set2Team2, setMatch3Set2Team2] = useState("");
  const [match3Set3Team1, setMatch3Set3Team1] = useState("");
  const [match3Set3Team2, setMatch3Set3Team2] = useState("");

  // Roster dialog state
  const [rosterDialogOpen, setRosterDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<NationsLeagueTeam | null>(
    null,
  );
  const [rosterPlayers, setRosterPlayers] = useState<NationsLeaguePlayer[]>([]);
  const [confirmFinalRoster, setConfirmFinalRoster] = useState(false);
  const [rosterBypass, setRosterBypass] = useState(false);
  const [rosterBypassReason, setRosterBypassReason] = useState("");
  const [matchDaysOpen, setMatchDaysOpen] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerImage, setNewPlayerImage] = useState<File | null>(null);
  const [newPlayerImagePreview, setNewPlayerImagePreview] =
    useState<string>("");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingImage, setEditingImage] = useState<File | null>(null);
  const [editingImagePreview, setEditingImagePreview] = useState<string>("");
  const [uploadingImage, setUploadingImage] = useState(false);
  // SPL players for the roster autocomplete; picking one links the roster entry to the SPL account
  const [splPlayers, setSplPlayers] = useState<Player[]>([]);
  const [selectedSplPlayer, setSelectedSplPlayer] = useState<Player | null>(null);

  // Date/time dialog state
  const [dateTimeDialogOpen, setDateTimeDialogOpen] = useState(false);
  const [selectedMatchForDateTime, setSelectedMatchForDateTime] =
    useState<NationsLeagueMatch | null>(null);
  const [matchDateTime, setMatchDateTime] = useState("");

  // Player assignment dialog state
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [selectedMatchForPlayers, setSelectedMatchForPlayers] =
    useState<NationsLeagueMatch | null>(null);
  const [match1Team1Player1, setMatch1Team1Player1] = useState("");
  const [match1Team1Player2, setMatch1Team1Player2] = useState("");
  const [match1Team2Player1, setMatch1Team2Player1] = useState("");
  const [match1Team2Player2, setMatch1Team2Player2] = useState("");
  const [match2Team1Player1, setMatch2Team1Player1] = useState("");
  const [match2Team1Player2, setMatch2Team1Player2] = useState("");
  const [match2Team2Player1, setMatch2Team2Player1] = useState("");
  const [match2Team2Player2, setMatch2Team2Player2] = useState("");
  const [match3Team1Player1, setMatch3Team1Player1] = useState("");
  const [match3Team1Player2, setMatch3Team1Player2] = useState("");
  const [match3Team2Player1, setMatch3Team2Player1] = useState("");
  const [match3Team2Player2, setMatch3Team2Player2] = useState("");

  // Did not compete state
  const [match1Team1DidNotCompete, setMatch1Team1DidNotCompete] =
    useState(false);
  const [match1Team2DidNotCompete, setMatch1Team2DidNotCompete] =
    useState(false);
  const [match2Team1DidNotCompete, setMatch2Team1DidNotCompete] =
    useState(false);
  const [match2Team2DidNotCompete, setMatch2Team2DidNotCompete] =
    useState(false);
  const [match3Team1DidNotCompete, setMatch3Team1DidNotCompete] =
    useState(false);
  const [match3Team2DidNotCompete, setMatch3Team2DidNotCompete] =
    useState(false);

  // Helper function to convert country code to flag emoji or return as-is if already emoji
  // Renders a real flag image (flag emoji show up as letters on Windows)
  const getFlagEmoji = (flag: string): React.ReactNode => <FlagIcon flag={flag} />;

  useEffect(() => {
    if (nationsLeagueId) fetchAllData();
  }, [nationsLeagueId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError("");

      // Public endpoints - no auth required
      const [
        teamsRes,
        matchesRes,
        standingsARes,
        standingsBRes,
        standingsCRes,
        lockStatusRes,
      ] = await Promise.all([
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/teams?includeBye=true`),
        // Admin token => unredacted player assignments
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/matches`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        }),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/standings/A`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/standings/B`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/standings/C`),
        axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}/lock-status`),
      ]);

      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
      setStandingsA(standingsARes.data);
      setStandingsB(standingsBRes.data);
      setStandingsC(standingsCRes.data);
      setIsLocked(lockStatusRes.data.isLocked);

      // Fetch announcement
      await fetchAnnouncement();
      await fetchEditionInfo();
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to load Nations League data",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncement = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/nations-league/${nationsLeagueId}/announcement`,
      );
      setAnnouncementMessage(response.data.message || "");
      setAnnouncementEnabled(response.data.isEnabled || false);
    } catch (err: any) {
      console.error("Error fetching announcement:", err);
    }
  };

  const handleSaveAnnouncement = async () => {
    try {
      setError("");
      setSuccess("");

      if (announcementEnabled && !announcementMessage.trim()) {
        setError("Announcement message cannot be empty when enabled");
        return;
      }

      const token = localStorage.getItem("authToken");
      const response = await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/announcement`,
        { message: announcementMessage, isEnabled: announcementEnabled },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess(response.data.success || "Announcement updated successfully");
    } catch (err: any) {
      console.error("Error saving announcement:", err);
      setError(err.response?.data?.error || "Failed to update announcement");
    }
  };

  const fetchEditionInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/nations-league/${nationsLeagueId}`);
      setEditionName(response.data.name || "");
      setInfoDescription(response.data.description || "");
      setInfoFaq(response.data.faq || []);
    } catch (err: any) {
      console.error("Error fetching edition info:", err);
    }
  };

  const handleSaveEditionInfo = async () => {
    try {
      setError("");
      setSuccess("");
      const token = localStorage.getItem("authToken");
      const response = await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/info`,
        { description: infoDescription, faq: infoFaq },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setInfoDescription(response.data.description);
      setInfoFaq(response.data.faq);
      setSuccess(response.data.success || "Edition info updated successfully");
      setInfoDialogOpen(false);
    } catch (err: any) {
      console.error("Error saving edition info:", err);
      setError(err.response?.data?.error || "Failed to update edition info");
    }
  };

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
  });

  // Refresh only the team list so the draw dialog stays open while nations are edited
  const refreshTeams = async () => {
    const res = await axios.get(
      `${API_URL}/api/nations-league/${nationsLeagueId}/teams?includeBye=true`,
    );
    setTeams(res.data);
  };

  // Pots: one pot per team slot in a group (4); pot size = number of groups. Teams start in list order
  // (BYE last = weakest pot) and the admin moves them so Pot 1 holds the strongest nations.
  const openDrawDialog = () => {
    const ordered = [...teams.filter((t) => !t.isBye), ...teams.filter((t) => t.isBye)];
    const potSize = teams.length / 4;
    setPots(
      [0, 1, 2, 3].map((p) =>
        ordered.slice(p * potSize, (p + 1) * potSize).map((t) => ({
          id: t.id,
          name: t.name,
          flag: t.flag,
          isBye: !!t.isBye,
        })),
      ),
    );
    setDrawStep("pots");
    setDrawPlan([]);
    setDrawDialogOpen(true);
  };

  const handleAddNation = async () => {
    if (!teamFormName.trim()) return;
    try {
      setError("");
      setTeamBusy(true);
      await axios.post(
        `${API_URL}/api/nations-league/${nationsLeagueId}/teams`,
        { name: teamFormName, flag: teamFormFlag },
        authHeaders(),
      );
      setAddNationOpen(false);
      await refreshTeams();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add nation");
    } finally {
      setTeamBusy(false);
    }
  };

  const handleAddBye = async () => {
    try {
      setError("");
      setTeamBusy(true);
      await axios.post(
        `${API_URL}/api/nations-league/${nationsLeagueId}/teams`,
        { isBye: true },
        authHeaders(),
      );
      await refreshTeams();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add BYE");
    } finally {
      setTeamBusy(false);
    }
  };

  const handleDeleteTeam = async (team: NationsLeagueTeam) => {
    if (!window.confirm(`Remove ${team.name} from this edition?`)) return;
    try {
      setError("");
      setTeamBusy(true);
      await axios.delete(
        `${API_URL}/api/nations-league/${nationsLeagueId}/teams/${team.id}`,
        authHeaders(),
      );
      setRosterDialogOpen(false);
      await refreshTeams();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove nation");
    } finally {
      setTeamBusy(false);
    }
  };

  const moveTeamBetweenPots = (teamId: string, from: number, to: number) => {
    setPots((prev) => {
      const next = prev.map((p) => [...p]);
      const team = next[from].find((t) => t.id === teamId);
      if (!team) return prev;
      next[from] = next[from].filter((t) => t.id !== teamId);
      next[to].push(team);
      return next;
    });
  };

  const potsValid =
    pots.length === 4 && pots.every((p) => p.length === drawGroupLetters.length);

  // Pre-compute the whole draw (pot by pot, one team into each group) and reveal it step by step
  const startDrawAnimation = () => {
    const plan: { team: DrawTeam; groupIdx: number }[] = [];
    pots.forEach((pot) => {
      const shuffled = [...pot].sort(() => Math.random() - 0.5);
      shuffled.forEach((team, groupIdx) => plan.push({ team, groupIdx }));
    });
    setDrawPlan(plan);
    setDrawRun((r) => r + 1);
    setDrawStep("animation");
  };

  const handleConfirmDraw = async () => {
    try {
      setError("");
      setSuccess("");
      setDrawing(true);
      const assignments: Record<string, string> = {};
      drawPlan.forEach(({ team, groupIdx }) => {
        assignments[team.id] = drawGroupLetters[groupIdx];
      });
      const response = await axios.post(
        `${API_URL}/api/nations-league/${nationsLeagueId}/draw`,
        { assignments },
        authHeaders(),
      );
      setSuccess(response.data.message || "Groups drawn");
      setDrawDialogOpen(false);
      fetchAllData();
    } catch (err: any) {
      console.error("Error drawing groups:", err);
      setError(err.response?.data?.error || "Failed to draw groups");
    } finally {
      setDrawing(false);
    }
  };

  const handleToggleLock = async () => {
    try {
      setError("");
      setSuccess("");
      const token = localStorage.getItem("authToken");

      if (!token) {
        setError("You must be logged in as an admin to toggle lock status.");
        return;
      }

      const newLockStatus = !isLocked;
      const response = await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/toggle-lock`,
        { isLocked: newLockStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setIsLocked(newLockStatus);
      setSuccess(response.data.message);
    } catch (err: any) {
      console.error("Toggle lock error:", err.response?.data || err.message);
      setError(err.response?.data?.error || "Failed to toggle lock status");
    }
  };

  const handleOpenKnockoutPreview = async () => {
    try {
      setError("");
      setSuccess("");
      const token = localStorage.getItem("authToken");

      if (!token) {
        setError("You must be logged in as an admin.");
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/nations-league/${nationsLeagueId}/knockout/preview`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setKnockoutPreview(response.data);
      setKnockoutPreviewOpen(true);
    } catch (err: any) {
      console.error(
        "Knockout preview error:",
        err.response?.data || err.message,
      );
      setError(err.response?.data?.error || "Failed to load knockout preview");
    }
  };

  const handleGenerateKnockout = async () => {
    try {
      setError("");
      setSuccess("");
      setGeneratingKnockout(true);
      const token = localStorage.getItem("authToken");

      if (!token) {
        setError("You must be logged in as an admin.");
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/nations-league/${nationsLeagueId}/knockout/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess(response.data.message);
      setKnockoutPreviewOpen(false);
      setKnockoutPreview(null);
      fetchAllData();
    } catch (err: any) {
      console.error(
        "Knockout generation error:",
        err.response?.data || err.message,
      );
      setError(
        err.response?.data?.error || "Failed to generate knockout matches",
      );
    } finally {
      setGeneratingKnockout(false);
    }
  };

  const handleOpenScoreDialog = (match: NationsLeagueMatch) => {
    setSelectedMatch(match);
    setSelectedIndividualMatch(1);

    // Check if matches array exists (new structure) or initialize with defaults
    const matches = match.matches || [
      {
        matchNumber: 1,
        sets: [
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
        ],
        isCompleted: false,
        winnerTeamId: null,
      },
      {
        matchNumber: 2,
        sets: [
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
        ],
        isCompleted: false,
        winnerTeamId: null,
      },
      {
        matchNumber: 3,
        sets: [
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
          { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
        ],
        isCompleted: false,
        winnerTeamId: null,
      },
    ];

    // Load didNotCompete flags from match data
    setMatch1Team1DidNotCompete(matches[0].team1DidNotCompete || false);
    setMatch1Team2DidNotCompete(matches[0].team2DidNotCompete || false);
    setMatch2Team1DidNotCompete(matches[1].team1DidNotCompete || false);
    setMatch2Team2DidNotCompete(matches[1].team2DidNotCompete || false);
    setMatch3Team1DidNotCompete(matches[2].team1DidNotCompete || false);
    setMatch3Team2DidNotCompete(matches[2].team2DidNotCompete || false);

    // Load Match 1 scores
    setMatch1Set1Team1(matches[0].sets[0].team1Games.toString());
    setMatch1Set1Team2(matches[0].sets[0].team2Games.toString());
    setMatch1Set2Team1(matches[0].sets[1].team1Games.toString());
    setMatch1Set2Team2(matches[0].sets[1].team2Games.toString());
    setMatch1Set3Team1(matches[0].sets[2].team1Games.toString());
    setMatch1Set3Team2(matches[0].sets[2].team2Games.toString());

    // Load Match 2 scores
    setMatch2Set1Team1(matches[1].sets[0].team1Games.toString());
    setMatch2Set1Team2(matches[1].sets[0].team2Games.toString());
    setMatch2Set2Team1(matches[1].sets[1].team1Games.toString());
    setMatch2Set2Team2(matches[1].sets[1].team2Games.toString());
    setMatch2Set3Team1(matches[1].sets[2].team1Games.toString());
    setMatch2Set3Team2(matches[1].sets[2].team2Games.toString());

    // Load Match 3 scores
    setMatch3Set1Team1(matches[2].sets[0].team1Games.toString());
    setMatch3Set1Team2(matches[2].sets[0].team2Games.toString());
    setMatch3Set2Team1(matches[2].sets[1].team1Games.toString());
    setMatch3Set2Team2(matches[2].sets[1].team2Games.toString());
    setMatch3Set3Team1(matches[2].sets[2].team1Games.toString());
    setMatch3Set3Team2(matches[2].sets[2].team2Games.toString());

    setScoreDialogOpen(true);
  };

  // Helper function to determine set winner (first to 6, must win by 2)
  const getSetWinner = (
    team1Score: number,
    team2Score: number,
  ): "team1" | "team2" | null => {
    // No scores entered yet
    if (team1Score === 0 && team2Score === 0) return null;

    // Standard win: first to 6 with at least 2-game lead
    if (team1Score >= 6 && team1Score - team2Score >= 2) return "team1";
    if (team2Score >= 6 && team2Score - team1Score >= 2) return "team2";

    // Tiebreak scenario: 7-6 or 6-7
    if (team1Score === 7 && team2Score === 6) return "team1";
    if (team2Score === 7 && team1Score === 6) return "team2";

    // No winner yet
    return null;
  };

  // Helper functions to check if Set 3 should be shown (1-1 after first two sets)
  const shouldShowMatch1Set3 = (): boolean => {
    const set1Team1 = parseInt(match1Set1Team1) || 0;
    const set1Team2 = parseInt(match1Set1Team2) || 0;
    const set2Team1 = parseInt(match1Set2Team1) || 0;
    const set2Team2 = parseInt(match1Set2Team2) || 0;

    const set1Winner = getSetWinner(set1Team1, set1Team2);
    const set2Winner = getSetWinner(set2Team1, set2Team2);

    // Show Set 3 if both sets have winners and they're different (1-1 tie)
    return !!(set1Winner && set2Winner && set1Winner !== set2Winner);
  };

  const shouldShowMatch2Set3 = (): boolean => {
    const set1Team1 = parseInt(match2Set1Team1) || 0;
    const set1Team2 = parseInt(match2Set1Team2) || 0;
    const set2Team1 = parseInt(match2Set2Team1) || 0;
    const set2Team2 = parseInt(match2Set2Team2) || 0;

    const set1Winner = getSetWinner(set1Team1, set1Team2);
    const set2Winner = getSetWinner(set2Team1, set2Team2);

    return !!(set1Winner && set2Winner && set1Winner !== set2Winner);
  };

  const shouldShowMatch3Set3 = (): boolean => {
    const set1Team1 = parseInt(match3Set1Team1) || 0;
    const set1Team2 = parseInt(match3Set1Team2) || 0;
    const set2Team1 = parseInt(match3Set2Team1) || 0;
    const set2Team2 = parseInt(match3Set2Team2) || 0;

    const set1Winner = getSetWinner(set1Team1, set1Team2);
    const set2Winner = getSetWinner(set2Team1, set2Team2);

    return !!(set1Winner && set2Winner && set1Winner !== set2Winner);
  };

  // Clear Set 3 scores when match is decided 2-0 (not showing Set 3)
  useEffect(() => {
    if (!shouldShowMatch1Set3()) {
      setMatch1Set3Team1("");
      setMatch1Set3Team2("");
    }
  }, [match1Set1Team1, match1Set1Team2, match1Set2Team1, match1Set2Team2]);

  useEffect(() => {
    if (!shouldShowMatch2Set3()) {
      setMatch2Set3Team1("");
      setMatch2Set3Team2("");
    }
  }, [match2Set1Team1, match2Set1Team2, match2Set2Team1, match2Set2Team2]);

  useEffect(() => {
    if (!shouldShowMatch3Set3()) {
      setMatch3Set3Team1("");
      setMatch3Set3Team2("");
    }
  }, [match3Set1Team1, match3Set1Team2, match3Set2Team1, match3Set2Team2]);

  const handleSaveScore = async () => {
    if (!selectedMatch) return;

    try {
      setError("");
      setSuccess("");

      // Helper function to determine match scores based on didNotCompete flags
      const getMatchSets = (
        matchNumber: 1 | 2 | 3,
        set1Team1: string,
        set1Team2: string,
        set2Team1: string,
        set2Team2: string,
        set3Team1: string,
        set3Team2: string,
        team1DNC: boolean,
        team2DNC: boolean,
      ) => {
        // Check if Match 3 is a super tiebreak
        const isSuperTiebreak = matchNumber === 3 && selectedMatch?.matches[2]?.isSuperTiebreakDecider;
        
        // If both teams did not compete, return empty scores (0-0)
        if (team1DNC && team2DNC) {
          return [
            { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
            { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
            { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
          ];
        }

        // If team1 did not compete
        if (team1DNC) {
          if (isSuperTiebreak) {
            // Super tiebreak: 0-10 in Set 1 only
            return [
              { team1Games: 0, team2Games: 10, isSuperTiebreak: true },
              { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
              { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
            ];
          } else {
            // Regular match: 6-0, 6-0
            return [
              { team1Games: 0, team2Games: 6, isSuperTiebreak: false },
              { team1Games: 0, team2Games: 6, isSuperTiebreak: false },
              { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
            ];
          }
        }

        // If team2 did not compete
        if (team2DNC) {
          if (isSuperTiebreak) {
            // Super tiebreak: 10-0 in Set 1 only
            return [
              { team1Games: 10, team2Games: 0, isSuperTiebreak: true },
              { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
              { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
            ];
          } else {
            // Regular match: 6-0, 6-0
            return [
              { team1Games: 6, team2Games: 0, isSuperTiebreak: false },
              { team1Games: 6, team2Games: 0, isSuperTiebreak: false },
              { team1Games: 0, team2Games: 0, isSuperTiebreak: false },
            ];
          }
        }

        // Normal match - use entered scores
        if (isSuperTiebreak) {
          // Super tiebreak: only Set 1 is used, Set 2 and 3 are 0-0
          return [
            {
              team1Games: parseInt(set1Team1) || 0,
              team2Games: parseInt(set1Team2) || 0,
              isSuperTiebreak: true,
            },
            {
              team1Games: 0,
              team2Games: 0,
              isSuperTiebreak: false,
            },
            {
              team1Games: 0,
              team2Games: 0,
              isSuperTiebreak: false,
            },
          ];
        } else {
          // Regular match - all 3 sets
          return [
            {
              team1Games: parseInt(set1Team1) || 0,
              team2Games: parseInt(set1Team2) || 0,
              isSuperTiebreak: false,
            },
            {
              team1Games: parseInt(set2Team1) || 0,
              team2Games: parseInt(set2Team2) || 0,
              isSuperTiebreak: false,
            },
            {
              team1Games: parseInt(set3Team1) || 0,
              team2Games: parseInt(set3Team2) || 0,
              isSuperTiebreak: false,
            },
          ];
        }
      };

      // Build all 3 matches with automatic score handling for didNotCompete
      const allMatches = [
        {
          matchNumber: 1,
          sets: getMatchSets(
            1,
            match1Set1Team1,
            match1Set1Team2,
            match1Set2Team1,
            match1Set2Team2,
            match1Set3Team1,
            match1Set3Team2,
            match1Team1DidNotCompete,
            match1Team2DidNotCompete,
          ),
        },
        {
          matchNumber: 2,
          sets: getMatchSets(
            2,
            match2Set1Team1,
            match2Set1Team2,
            match2Set2Team1,
            match2Set2Team2,
            match2Set3Team1,
            match2Set3Team2,
            match2Team1DidNotCompete,
            match2Team2DidNotCompete,
          ),
        },
        {
          matchNumber: 3,
          sets: getMatchSets(
            3,
            match3Set1Team1,
            match3Set1Team2,
            match3Set2Team1,
            match3Set2Team2,
            match3Set3Team1,
            match3Set3Team2,
            match3Team1DidNotCompete,
            match3Team2DidNotCompete,
          ),
        },
      ];

      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/matches/${selectedMatch.id}`,
        { matches: allMatches },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess("Scores updated successfully");
      setScoreDialogOpen(false);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update scores");
    }
  };

  const handleMarkAsDraw = async () => {
    if (!selectedMatch) return;

    const confirmed = window.confirm(
      `Mark this matchup as a draw?\n\n${selectedMatch.team1Name} vs ${selectedMatch.team2Name}\n\nBoth teams will receive 1 point.`
    );

    if (!confirmed) return;

    try {
      setError("");
      setSuccess("");

      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/matches/${selectedMatch.id}/mark-draw`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess("Matchup marked as draw successfully");
      setScoreDialogOpen(false);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to mark matchup as draw");
    }
  };

  const handleOpenRosterDialog = (team: NationsLeagueTeam) => {
    setSelectedTeam(team);
    // Convert old string format to new object format for backward compatibility
    const playersArray = (team.players || []).map((player) => {
      if (typeof player === "string") {
        return { name: player, profilePicture: undefined };
      }
      return player;
    });
    setRosterPlayers(playersArray);
    setNewPlayerName("");
    setNewPlayerImage(null);
    setNewPlayerImagePreview("");
    setSelectedSplPlayer(null);
    setTeamFormName(team.name);
    setTeamFormFlag(team.flag);
    setConfirmFinalRoster(false);
    setRosterBypass(false);
    setRosterBypassReason("");
    setRosterDialogOpen(true);
    if (splPlayers.length === 0) {
      axios
        .get(`${API_URL}/api/players`)
        .then((res) => setSplPlayers(res.data))
        .catch((err) => console.error("Error loading SPL players:", err));
    }
  };

  const handlePlayerImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isEditing: boolean = false,
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.onloadend = () => {
        if (isEditing) {
          setEditingImage(file);
          setEditingImagePreview(reader.result as string);
        } else {
          setNewPlayerImage(file);
          setNewPlayerImagePreview(reader.result as string);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  const handlePasteInNewPlayer = async (e: React.ClipboardEvent) => {
    // Check if there are any files (images) in the clipboard
    const items = e.clipboardData?.items;
    if (!items) return;

    let imageFound = false;

    // Look for images in clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf("image") !== -1) {
        e.preventDefault(); // Prevent default paste behavior for images
        imageFound = true;

        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setNewPlayerImage(file);
            setNewPlayerImagePreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }

    // If no image was found, let default text paste behavior happen
    // (text will be pasted into the TextField naturally)
  };

  const handleAddPlayer = async () => {
    if (!newPlayerName.trim()) return;

    try {
      setUploadingImage(true);
      // Default to the SPL profile picture when a linked player was picked
      let profilePictureUrl = selectedSplPlayer?.profilePicture || "";

      if (newPlayerImage) {
        profilePictureUrl = await ImageUploadService.uploadProfilePicture(
          newPlayerImage,
          `nations-league/${selectedTeam?.id}`,
        );
      }

      const newPlayer: NationsLeaguePlayer = {
        name: newPlayerName.trim(),
        profilePicture: profilePictureUrl,
        ...(selectedSplPlayer && { playerId: selectedSplPlayer.id }),
      };

      setRosterPlayers([...rosterPlayers, newPlayer]);
      setNewPlayerName("");
      setNewPlayerImage(null);
      setNewPlayerImagePreview("");
      setSelectedSplPlayer(null);
    } catch (err) {
      setError("Failed to upload player image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemovePlayer = (index: number) => {
    setRosterPlayers(rosterPlayers.filter((_, i) => i !== index));
  };

  const handlePasteInEdit = async (e: React.ClipboardEvent) => {
    // Check if there are any files (images) in the clipboard
    const items = e.clipboardData?.items;
    if (!items) return;

    let imageFound = false;

    // Look for images in clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf("image") !== -1) {
        e.preventDefault(); // Prevent default paste behavior for images
        imageFound = true;

        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setEditingImage(file);
            setEditingImagePreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }

    // If no image was found, let default text paste behavior happen
    // (text will be pasted into the TextField naturally)
  };

  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditingName(rosterPlayers[index].name);
    setEditingImage(null);
    setEditingImagePreview(rosterPlayers[index].profilePicture || "");
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingName("");
    setEditingImage(null);
    setEditingImagePreview("");
  };

  const handleSaveEdit = async () => {
    if (editingIndex !== null && editingName.trim()) {
      try {
        setUploadingImage(true);
        let profilePictureUrl =
          rosterPlayers[editingIndex].profilePicture || "";

        if (editingImage) {
          profilePictureUrl = await ImageUploadService.uploadProfilePicture(
            editingImage,
            `nations-league/${selectedTeam?.id}`,
          );
        }

        const newPlayers = [...rosterPlayers];
        // Renaming a linked player means it is no longer that SPL account
        const existing = rosterPlayers[editingIndex];
        const keepLink = existing.playerId && existing.name === editingName.trim();
        newPlayers[editingIndex] = {
          name: editingName.trim(),
          profilePicture: profilePictureUrl,
          ...(keepLink && { playerId: existing.playerId }),
        };
        setRosterPlayers(newPlayers);
        setEditingIndex(null);
        setEditingName("");
        setEditingImage(null);
        setEditingImagePreview("");
      } catch (err) {
        setError("Failed to upload player image");
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newPlayers = [...rosterPlayers];
    const draggedPlayer = newPlayers[draggedIndex];

    // Remove from old position
    newPlayers.splice(draggedIndex, 1);

    // Insert at new position
    newPlayers.splice(dropIndex, 0, draggedPlayer);

    setRosterPlayers(newPlayers);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const currentRosterPhase: "group" | "knockout" = matches.some(
    (m) => m.stage !== "group",
  )
    ? "knockout"
    : "group";
  const rosterLimitReached =
    !!selectedTeam?.rosterLocked &&
    (selectedTeam.rosterChanges?.[currentRosterPhase] ?? 0) >= 1;

  const handleSaveRoster = async () => {
    if (!selectedTeam) return;

    try {
      setError("");
      setSuccess("");

      const token = localStorage.getItem("authToken");
      // Before the draw the nation's name/flag can be corrected in the same dialog
      if (!groupsDrawn && !selectedTeam.isBye &&
          (teamFormName.trim() !== selectedTeam.name || teamFormFlag !== selectedTeam.flag)) {
        await axios.put(
          `${API_URL}/api/nations-league/${nationsLeagueId}/teams/${selectedTeam.id}`,
          { name: teamFormName, flag: teamFormFlag },
          authHeaders(),
        );
      }
      await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/teams/${selectedTeam.id}/roster`,
        {
          players: rosterPlayers,
          ...(!selectedTeam.rosterLocked && confirmFinalRoster && { confirmFinal: true }),
          ...(selectedTeam.rosterLocked && rosterBypass && {
            bypass: true,
            bypassReason: rosterBypassReason,
          }),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess(
        !selectedTeam.rosterLocked && confirmFinalRoster
          ? "Initial roster saved and locked as final"
          : "Team roster updated successfully",
      );
      setRosterDialogOpen(false);
      await refreshTeams();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update roster");
    }
  };

  const handleOpenDateTimeDialog = (match: NationsLeagueMatch) => {
    setSelectedMatchForDateTime(match);
    // Format the date for datetime-local input
    if (match.matchDateTime) {
      const date = new Date(match.matchDateTime);
      const formatted = date.toISOString().slice(0, 16);
      setMatchDateTime(formatted);
    } else {
      setMatchDateTime("");
    }
    setDateTimeDialogOpen(true);
  };

  const handleSaveDateTime = async () => {
    if (!selectedMatchForDateTime) return;

    try {
      setError("");
      setSuccess("");

      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/matches/${selectedMatchForDateTime.id}/datetime`,
        { matchDateTime: matchDateTime || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess("Match date/time updated successfully");
      setDateTimeDialogOpen(false);
      fetchAllData();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update date/time");
    }
  };

  const getTeamRoster = (teamId: string): string[] => {
    const team = teams.find((t) => t.id === teamId);
    return (
      team?.players?.map((p) => (typeof p === "string" ? p : p.name)) || []
    );
  };

  // Helper function to get assigned players across all matches for a team
  const getAssignedPlayersForTeam = (isTeam1: boolean): string[] => {
    const assigned: string[] = [];
    if (isTeam1) {
      if (match1Team1Player1) assigned.push(match1Team1Player1);
      if (match1Team1Player2) assigned.push(match1Team1Player2);
      if (match2Team1Player1) assigned.push(match2Team1Player1);
      if (match2Team1Player2) assigned.push(match2Team1Player2);
      if (match3Team1Player1) assigned.push(match3Team1Player1);
      if (match3Team1Player2) assigned.push(match3Team1Player2);
    } else {
      if (match1Team2Player1) assigned.push(match1Team2Player1);
      if (match1Team2Player2) assigned.push(match1Team2Player2);
      if (match2Team2Player1) assigned.push(match2Team2Player1);
      if (match2Team2Player2) assigned.push(match2Team2Player2);
      if (match3Team2Player1) assigned.push(match3Team2Player1);
      if (match3Team2Player2) assigned.push(match3Team2Player2);
    }
    return assigned;
  };

  // Helper function to check if this is a super tiebreak scenario
  // (semifinal or final, and first 2 matches are complete and tied 1-1)
  const isSuperTiebreakScenario = (): boolean => {
    if (!selectedMatchForPlayers) return false;
    
    const stage = selectedMatchForPlayers.stage;
    const isSemiFinalOrFinal = 
      stage === 'semifinal_top' || 
      stage === 'semifinal_bottom' || 
      stage === 'final_top' || 
      stage === 'final_bottom';
    
    if (!isSemiFinalOrFinal) return false;
    
    // Check if Match 1 and Match 2 are complete
    if (!isMatchComplete(1) || !isMatchComplete(2)) return false;
    
    // Check if the matchup is tied 1-1
    return selectedMatchForPlayers.team1MatchesWon === 1 && 
           selectedMatchForPlayers.team2MatchesWon === 1;
  };

  // Get available players for super tiebreak (from Match 1 and Match 2)
  const getAvailablePlayersForSuperTiebreak = (
    isTeam1: boolean,
    position: 1 | 2,
    currentValue: string
  ): string[] => {
    // Match 3 is unrestricted: offer the whole roster
    const players: string[] = selectedMatchForPlayers
      ? getTeamRoster(
          isTeam1
            ? selectedMatchForPlayers.team1Id
            : selectedMatchForPlayers.team2Id,
        )
      : [];

    // Filter out the partner in THIS match (can't pick same player twice)
    const partnerValue = position === 1 
      ? (isTeam1 ? match3Team1Player2 : match3Team2Player2)
      : (isTeam1 ? match3Team1Player1 : match3Team2Player1);
    
    return players.filter(p => p === currentValue || p !== partnerValue);
  };


  // Check if a match is complete (both teams have both players assigned OR one team did not compete)
  const isMatchComplete = (matchNumber: 1 | 2 | 3): boolean => {
    if (matchNumber === 1) {
      // Match 1 is complete if players are assigned OR if one team did not compete
      const hasPlayers = !!(
        match1Team1Player1 &&
        match1Team1Player2 &&
        match1Team2Player1 &&
        match1Team2Player2
      );
      const hasDidNotCompete =
        match1Team1DidNotCompete || match1Team2DidNotCompete;
      return hasPlayers || hasDidNotCompete;
    } else if (matchNumber === 2) {
      const hasPlayers = !!(
        match2Team1Player1 &&
        match2Team1Player2 &&
        match2Team2Player1 &&
        match2Team2Player2
      );
      const hasDidNotCompete =
        match2Team1DidNotCompete || match2Team2DidNotCompete;
      return hasPlayers || hasDidNotCompete;
    } else {
      const hasPlayers = !!(
        match3Team1Player1 &&
        match3Team1Player2 &&
        match3Team2Player1 &&
        match3Team2Player2
      );
      const hasDidNotCompete =
        match3Team1DidNotCompete || match3Team2DidNotCompete;
      return hasPlayers || hasDidNotCompete;
    }
  };

  // Check if one nation's side of a match is filled in (both players OR did not compete)
  const isSideComplete = (matchNumber: 1 | 2 | 3, isTeam1: boolean): boolean => {
    const slots = {
      1: {
        true: [match1Team1Player1, match1Team1Player2, match1Team1DidNotCompete],
        false: [match1Team2Player1, match1Team2Player2, match1Team2DidNotCompete],
      },
      2: {
        true: [match2Team1Player1, match2Team1Player2, match2Team1DidNotCompete],
        false: [match2Team2Player1, match2Team2Player2, match2Team2DidNotCompete],
      },
      3: {
        true: [match3Team1Player1, match3Team1Player2, match3Team1DidNotCompete],
        false: [match3Team2Player1, match3Team2Player2, match3Team2DidNotCompete],
      },
    }[matchNumber][String(isTeam1) as "true" | "false"];
    const [p1, p2, didNotCompete] = slots as [string, string, boolean];
    return !!(p1 && p2) || didNotCompete;
  };

  // Get the lowest ranked player (highest index) from a match
  const getLowestRankedPlayerFromMatch = (
    matchNumber: 1 | 2,
    isTeam1: boolean,
  ): number => {
    const roster = getTeamRoster(
      selectedMatchForPlayers
        ? isTeam1
          ? selectedMatchForPlayers.team1Id
          : selectedMatchForPlayers.team2Id
        : "",
    );

    let player1 = "";
    let player2 = "";

    if (isTeam1) {
      if (matchNumber === 1) {
        player1 = match1Team1Player1;
        player2 = match1Team1Player2;
      } else if (matchNumber === 2) {
        player1 = match2Team1Player1;
        player2 = match2Team1Player2;
      }
    } else {
      if (matchNumber === 1) {
        player1 = match1Team2Player1;
        player2 = match1Team2Player2;
      } else if (matchNumber === 2) {
        player1 = match2Team2Player1;
        player2 = match2Team2Player2;
      }
    }

    const rank1 = player1 ? roster.indexOf(player1) : -1;
    const rank2 = player2 ? roster.indexOf(player2) : -1;

    // Return the highest index (lowest rank) - if both exist, return the higher index
    if (rank1 >= 0 && rank2 >= 0) {
      return Math.max(rank1, rank2);
    } else if (rank1 >= 0) {
      return rank1;
    } else if (rank2 >= 0) {
      return rank2;
    }

    return -1; // No players selected yet
  };

  // Get available players for a specific position with smart consecutive pairing
  const getAvailablePlayersForPosition = (
    teamId: string,
    isTeam1: boolean,
    matchNumber: 1 | 2 | 3,
    position: 1 | 2,
    currentValue: string,
  ): string[] => {
    const roster = getTeamRoster(teamId);

    // Get the pair partner's value for this match
    let partnerValue = "";
    if (isTeam1) {
      if (matchNumber === 1)
        partnerValue = position === 1 ? match1Team1Player2 : match1Team1Player1;
      if (matchNumber === 2)
        partnerValue = position === 1 ? match2Team1Player2 : match2Team1Player1;
      if (matchNumber === 3)
        partnerValue = position === 1 ? match3Team1Player2 : match3Team1Player1;
    } else {
      if (matchNumber === 1)
        partnerValue = position === 1 ? match1Team2Player2 : match1Team2Player1;
      if (matchNumber === 2)
        partnerValue = position === 1 ? match2Team2Player2 : match2Team2Player1;
      if (matchNumber === 3)
        partnerValue = position === 1 ? match3Team2Player2 : match3Team2Player1;
    }

    // Get rank restrictions based on previous matches
    let minRankIndex = -1; // Minimum index (players must be AFTER this index)

    if (matchNumber === 2) {
      // Match 2: Can only select players ranked LOWER than the lowest ranked player in Match 1
      minRankIndex = getLowestRankedPlayerFromMatch(1, isTeam1);
    }
    // Match 3 is unrestricted: any roster player can be entered

    // Filter available players
    const available = roster.filter((player, index) => {
      // Always allow current value
      if (player === currentValue) return true;

      // Don't allow if it's the partner in THIS match (can't pick same player twice)
      if (player === partnerValue && partnerValue !== "") return false;

      // For Match 2 and 3: Only allow players with rank LOWER than the threshold
      // (Higher index in roster = lower rank)
      if (minRankIndex >= 0 && index <= minRankIndex) {
        return false;
      }

      return true;
    });

    return available;
  };

  // Get suggested pair based on ranking and availability
  const getSuggestedPair = (
    teamId: string,
    isTeam1: boolean,
    matchNumber: 1 | 2 | 3,
  ): [string, string] | null => {
    const roster = getTeamRoster(teamId);
    const assigned = getAssignedPlayersForTeam(isTeam1);

    // Get available players (not assigned yet)
    const available = roster.filter((p) => !assigned.includes(p));

    if (available.length < 2) return null;

    // For consecutive pairing, take the first 2 available players
    return [available[0], available[1]];
  };

  // Auto-suggest players when dialog opens or when a pair is completed
  const autoSuggestPlayers = (
    teamId: string,
    isTeam1: boolean,
    matchNumber: 1 | 2 | 3,
  ) => {
    const suggestion = getSuggestedPair(teamId, isTeam1, matchNumber);
    if (!suggestion) return;

    const [player1, player2] = suggestion;

    if (isTeam1) {
      if (matchNumber === 1) {
        if (!match1Team1Player1) setMatch1Team1Player1(player1);
        if (!match1Team1Player2) setMatch1Team1Player2(player2);
      } else if (matchNumber === 2) {
        if (!match2Team1Player1) setMatch2Team1Player1(player1);
        if (!match2Team1Player2) setMatch2Team1Player2(player2);
      } else if (matchNumber === 3) {
        if (!match3Team1Player1) setMatch3Team1Player1(player1);
        if (!match3Team1Player2) setMatch3Team1Player2(player2);
      }
    } else {
      if (matchNumber === 1) {
        if (!match1Team2Player1) setMatch1Team2Player1(player1);
        if (!match1Team2Player2) setMatch1Team2Player2(player2);
      } else if (matchNumber === 2) {
        if (!match2Team2Player1) setMatch2Team2Player1(player1);
        if (!match2Team2Player2) setMatch2Team2Player2(player2);
      } else if (matchNumber === 3) {
        if (!match3Team2Player1) setMatch3Team2Player1(player1);
        if (!match3Team2Player2) setMatch3Team2Player2(player2);
      }
    }
  };

  const handleOpenPlayerDialog = (match: NationsLeagueMatch) => {
    setSelectedMatchForPlayers(match);

    // Load existing player assignments if they exist
    const matches = match.matches || [];

    // Match 1
    setMatch1Team1Player1(matches[0]?.team1Player1 || "");
    setMatch1Team1Player2(matches[0]?.team1Player2 || "");
    setMatch1Team2Player1(matches[0]?.team2Player1 || "");
    setMatch1Team2Player2(matches[0]?.team2Player2 || "");
    setMatch1Team1DidNotCompete(matches[0]?.team1DidNotCompete || false);
    setMatch1Team2DidNotCompete(matches[0]?.team2DidNotCompete || false);

    // Match 2
    setMatch2Team1Player1(matches[1]?.team1Player1 || "");
    setMatch2Team1Player2(matches[1]?.team1Player2 || "");
    setMatch2Team2Player1(matches[1]?.team2Player1 || "");
    setMatch2Team2Player2(matches[1]?.team2Player2 || "");
    setMatch2Team1DidNotCompete(matches[1]?.team1DidNotCompete || false);
    setMatch2Team2DidNotCompete(matches[1]?.team2DidNotCompete || false);

    // Match 3
    setMatch3Team1Player1(matches[2]?.team1Player1 || "");
    setMatch3Team1Player2(matches[2]?.team1Player2 || "");
    setMatch3Team2Player1(matches[2]?.team2Player1 || "");
    setMatch3Team2Player2(matches[2]?.team2Player2 || "");
    setMatch3Team1DidNotCompete(matches[2]?.team1DidNotCompete || false);
    setMatch3Team2DidNotCompete(matches[2]?.team2DidNotCompete || false);

    setPlayerDialogOpen(true);
  };

  const handleClearAllPlayers = () => {
    // Clear all Match 1 players
    setMatch1Team1Player1("");
    setMatch1Team1Player2("");
    setMatch1Team2Player1("");
    setMatch1Team2Player2("");
    setMatch1Team1DidNotCompete(false);
    setMatch1Team2DidNotCompete(false);

    // Clear all Match 2 players
    setMatch2Team1Player1("");
    setMatch2Team1Player2("");
    setMatch2Team2Player1("");
    setMatch2Team2Player2("");
    setMatch2Team1DidNotCompete(false);
    setMatch2Team2DidNotCompete(false);

    // Clear all Match 3 players
    setMatch3Team1Player1("");
    setMatch3Team1Player2("");
    setMatch3Team2Player1("");
    setMatch3Team2Player2("");
    setMatch3Team1DidNotCompete(false);
    setMatch3Team2DidNotCompete(false);
  };

  const handleSavePlayerAssignments = async () => {
    if (!selectedMatchForPlayers) return;

    try {
      setError("");
      setSuccess("");

      const token = localStorage.getItem("authToken");

      const matches = [
        {
          matchNumber: 1,
          team1Player1: match1Team1Player1 || null,
          team1Player2: match1Team1Player2 || null,
          team2Player1: match1Team2Player1 || null,
          team2Player2: match1Team2Player2 || null,
          team1DidNotCompete: match1Team1DidNotCompete,
          team2DidNotCompete: match1Team2DidNotCompete,
        },
        {
          matchNumber: 2,
          team1Player1: match2Team1Player1 || null,
          team1Player2: match2Team1Player2 || null,
          team2Player1: match2Team2Player1 || null,
          team2Player2: match2Team2Player2 || null,
          team1DidNotCompete: match2Team1DidNotCompete,
          team2DidNotCompete: match2Team2DidNotCompete,
        },
        {
          matchNumber: 3,
          team1Player1: match3Team1Player1 || null,
          team1Player2: match3Team1Player2 || null,
          team2Player1: match3Team2Player1 || null,
          team2Player2: match3Team2Player2 || null,
          team1DidNotCompete: match3Team1DidNotCompete,
          team2DidNotCompete: match3Team2DidNotCompete,
          isSuperTiebreakDecider: isSuperTiebreakScenario(), // Mark as super tiebreak if applicable
        },
      ];

      await axios.put(
        `${API_URL}/api/nations-league/${nationsLeagueId}/matches/${selectedMatchForPlayers.id}/players`,
        { matches },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setSuccess("Player assignments updated successfully");
      setPlayerDialogOpen(false);
      fetchAllData();
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to update player assignments",
      );
    }
  };

  const getMatchesForTab = (): NationsLeagueMatch[] => {
    if (currentTab === 0) {
      // Group A
      return matches.filter((m) => m.stage === "group" && m.group === "A");
    } else if (currentTab === 1) {
      // Group B
      return matches.filter((m) => m.stage === "group" && m.group === "B");
    } else if (hasGroupC && currentTab === 2) {
      // Group C
      return matches.filter((m) => m.stage === "group" && m.group === "C");
    } else if (currentTab === koBase) {
      // Semifinals (both top and bottom)
      return matches.filter((m) => m.stage === "semifinal_top" || m.stage === "semifinal_bottom");
    } else if (currentTab === koBase + 1) {
      // Third Place (both 3rd and 7th)
      return matches.filter((m) => m.stage === "third_place_top" || m.stage === "third_place_bottom");
    } else if (currentTab === koBase + 2) {
      // Finals (both top and bottom)
      return matches.filter((m) => m.stage === "final_top" || m.stage === "final_bottom");
    }
    return [];
  };

  const getTeamsForCurrentTab = (): NationsLeagueTeam[] => {
    if (currentTab === 0) {
      // Group A
      return teams.filter((t) => t.group === "A" && !t.isBye);
    } else if (currentTab === 1) {
      // Group B
      return teams.filter((t) => t.group === "B" && !t.isBye);
    } else if (hasGroupC && currentTab === 2) {
      // Group C
      return teams.filter((t) => t.group === "C" && !t.isBye);
    } else {
      // For knockout stages, get teams from the current matches
      const currentMatches = getMatchesForTab();
      const teamIds = new Set<string>();
      currentMatches.forEach((m) => {
        teamIds.add(m.team1Id);
        teamIds.add(m.team2Id);
      });
      return teams.filter((t) => teamIds.has(t.id));
    }
  };

  // Check if a match can have scores entered/edited
  const canEditScores = (match: NationsLeagueMatch): boolean => {
    if (!match.matches) return false;

    // Allow score editing if:
    // 1. At least one individual match has all 4 players assigned
    // 2. OR any "did not compete" flags are set
    // 3. OR the match has any progress (status is In Progress or Completed)
    // 4. OR there are any match wins recorded
    // Both nations must be listed (players or did-not-compete) before scores can be entered
    const hasPlayers = match.matches.some(
      (m) =>
        ((m.team1Player1 && m.team1Player2) || m.team1DidNotCompete) &&
        ((m.team2Player1 && m.team2Player2) || m.team2DidNotCompete),
    );
    
    const hasProgress = 
      match.status === 'In Progress' || 
      match.status === 'Completed' || 
      match.status === 'Completed (Draw)' ||
      (match.team1MatchesWon !== undefined && match.team1MatchesWon > 0) ||
      (match.team2MatchesWon !== undefined && match.team2MatchesWon > 0);

    return hasPlayers || hasProgress;
  };

  // Keep old function for backward compatibility
  const hasPlayersAssigned = (match: NationsLeagueMatch): boolean => {
    return canEditScores(match);
  };

  const renderCountryFlags = () => {
    const currentTeams = getTeamsForCurrentTab();

    if (currentTeams.length === 0) return null;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
          mb: 4,
          flexWrap: "wrap",
          p: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        }}
      >
        {currentTeams.map((team, index) => (
          <Box
            key={team.id}
            sx={{
              position: "relative",
              animation: "fadeInUp 0.6s ease-out",
              animationDelay: `${index * 0.1}s`,
              animationFillMode: "both",
              "@keyframes fadeInUp": {
                from: {
                  opacity: 0,
                  transform: "translateY(20px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                cursor: "pointer",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-8px) scale(1.05)",
                  "& .flag-image": {
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
                  },
                },
              }}
              onClick={() => handleOpenRosterDialog(team)}
            >
              <Box
                className="flag-image"
                sx={{
                  width: { xs: 80, sm: 100, md: 120 },
                  height: { xs: 80, sm: 100, md: 120 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                  borderRadius: "50%",
                  border: "4px solid white",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                  bgcolor: "background.paper",
                  transition: "box-shadow 0.3s ease",
                }}
              >
                {getFlagEmoji(team.flag)}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: -24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "white",
                  fontWeight: "bold",
                  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                }}
              >
                {team.name}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderStandingsTable = (standings: NationsLeagueStandings[]) => (
    <TableContainer component={Paper} sx={{ mb: 3, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: { xs: 800, md: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Pos</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Team</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>Players</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>MP</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>MW</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>MD</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>ML</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>SW</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>SL</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>SD</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>GW</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>GL</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>GD</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>Pts</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {standings.map((standing) => {
            const team = teams.find((t) => t.id === standing.teamId);
            return (
              <TableRow key={standing.teamId}>
                <TableCell>{standing.position}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>{getFlagEmoji(standing.flag)}</span>
                    <span>{standing.teamName}</span>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {team?.players?.length || 0}
                </TableCell>
                <TableCell align="center">{standing.matchesPlayed}</TableCell>
                <TableCell align="center">{standing.matchesWon}</TableCell>
                <TableCell align="center">{standing.matchesDrawn || 0}</TableCell>
                <TableCell align="center">{standing.matchesLost}</TableCell>
                <TableCell align="center">{standing.setsWon}</TableCell>
                <TableCell align="center">{standing.setsLost}</TableCell>
                <TableCell align="center">
                  {standing.setDifference > 0 ? "+" : ""}
                  {standing.setDifference}
                </TableCell>
                <TableCell align="center">{standing.gamesWon}</TableCell>
                <TableCell align="center">{standing.gamesLost}</TableCell>
                <TableCell align="center">
                  {standing.gameDifference > 0 ? "+" : ""}
                  {standing.gameDifference}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                  {standing.points}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => team && handleOpenRosterDialog(team)}
                    title="Edit Roster"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMatchesAsMobileCards = (matches: NationsLeagueMatch[]) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {matches.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">No matches yet</Typography>
        </Paper>
      ) : (
        matches.map((match) => (
          <Card key={match.id} sx={{ width: "100%" }}>
            <CardContent>
              {/* Matchup Number */}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                Matchup #{match.matchNumber || "-"}
              </Typography>

              {/* Teams */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {match.team1Flag && <span style={{ fontSize: "1.5rem" }}>{getFlagEmoji(match.team1Flag)}</span>}
                <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}>
                  {match.team1Name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  vs
                </Typography>
                <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}>
                  {match.team2Name}
                </Typography>
                {match.team2Flag && <span style={{ fontSize: "1.5rem" }}>{getFlagEmoji(match.team2Flag)}</span>}
              </Box>

              {/* Date & Time */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Date & Time:
                </Typography>
                {match.matchDateTime ? (
                  <Typography variant="body2">
                    {new Date(match.matchDateTime).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    Not set
                  </Typography>
                )}
              </Box>

              {/* Result */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Result:
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5 }}>
                  {match.team1MatchesWon ?? 0} - {match.team2MatchesWon ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (best of 3 matches)
                </Typography>
              </Box>

              {/* Status */}
              <Box sx={{ mb: 2 }}>
                {match.status === "Completed (Draw)" || match.isDraw ? (
                  <Chip label="🤝 Completed - Draw" color="info" size="small" />
                ) : match.status === "Completed" && match.winnerTeamId ? (
                  <Chip 
                    label={`🏆 ${match.winnerTeamId === match.team1Id ? match.team1Name : match.team2Name} Won`}
                    color="success" 
                    size="small" 
                  />
                ) : match.status === "In Progress" ? (
                  <Chip label="In Progress" color="warning" size="small" />
                ) : (
                  <Chip label="Scheduled" color="default" size="small" />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Actions */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenDateTimeDialog(match)}
                  fullWidth
                >
                  Set Date/Time
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenPlayerDialog(match)}
                  fullWidth
                >
                  Assign Players
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenScoreDialog(match)}
                  disabled={!hasPlayersAssigned(match)}
                  fullWidth
                >
                  {hasPlayersAssigned(match) ? "Edit Scores" : "Assign Players First"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );

  const renderMatchesTable = (matches: NationsLeagueMatch[]) => (
    <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: { xs: 650, md: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Matchup</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Teams</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Date & Time</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>Result</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>Status</TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap", minWidth: { xs: 130, sm: "auto" } }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {matches.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No matches yet
              </TableCell>
            </TableRow>
          ) : (
            matches.map((match) => (
              <TableRow key={match.id}>
                <TableCell>#{match.matchNumber || "-"}</TableCell>
                <TableCell sx={{ minWidth: { xs: 140, sm: "auto" } }}>
                  <Box sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: { xs: 0.5, sm: 1 },
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    fontSize: { xs: "0.85rem", sm: "1rem" }
                  }}>
                    {match.team1Flag && <span>{getFlagEmoji(match.team1Flag)}</span>}
                    <strong>{match.team1Name}</strong>
                    <span>vs</span>
                    <strong>{match.team2Name}</strong>
                    {match.team2Flag && <span>{getFlagEmoji(match.team2Flag)}</span>}
                  </Box>
                </TableCell>
                <TableCell>
                  {match.matchDateTime ? (
                    <Typography variant="body2">
                      {new Date(match.matchDateTime).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not set
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {match.team1MatchesWon ?? 0} -{" "}
                      {match.team2MatchesWon ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (best of 3 matches)
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {match.status === "Completed (Draw)" || match.isDraw ? (
                    <Chip label="🤝 Completed - Draw" color="info" size="small" />
                  ) : match.status === "Completed" && match.winnerTeamId ? (
                    <Chip 
                      label={`🏆 Completed - ${match.winnerTeamId === match.team1Id ? match.team1Name : match.team2Name}`}
                      color="success" 
                      size="small" 
                    />
                  ) : match.status === "In Progress" ? (
                    <Chip label="In Progress" color="warning" size="small" />
                  ) : (
                    <Chip label="Scheduled" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{ display: "flex", gap: 0.5, flexDirection: { xs: "column", sm: "row" }, minWidth: { xs: 120, sm: "auto" } }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenDateTimeDialog(match)}
                      fullWidth
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                    >
                      Date/Time
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenPlayerDialog(match)}
                      fullWidth
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                    >
                      Players
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenScoreDialog(match)}
                      disabled={!hasPlayersAssigned(match)}
                      fullWidth
                      title={
                        !hasPlayersAssigned(match)
                          ? "Assign players first before adding scores"
                          : "Edit match scores"
                      }
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.8125rem" } }}
                    >
                      Scores
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
          🏆 Nations League
        </Typography>
        <Box sx={{ display: "flex", gap: { xs: 1, md: 2 }, alignItems: "center", flexWrap: "wrap", width: { xs: "100%", md: "auto" } }}>
          {teams.length > 0 && (
            <>
              <Button
                variant={isLocked ? "contained" : "outlined"}
                color={isLocked ? "error" : "success"}
                startIcon={isLocked ? <LockIcon /> : <LockOpenIcon />}
                onClick={handleToggleLock}
                size="small"
                sx={{ flex: { xs: "1 1 auto", sm: "0 0 auto" } }}
              >
                {isLocked ? "Locked" : "Unlocked"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CampaignIcon />}
                onClick={() => setAnnouncementDialogOpen(true)}
                size="small"
                sx={{ flex: { xs: "1 1 auto", sm: "0 0 auto" } }}
              >
                Announcement
              </Button>
              <Button
                variant="outlined"
                startIcon={<EventIcon />}
                onClick={() => setMatchDaysOpen(true)}
                size="small"
                sx={{ flex: { xs: "1 1 auto", sm: "0 0 auto" } }}
              >
                Match Days
              </Button>
              <Button
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                onClick={() => setInfoDialogOpen(true)}
                size="small"
                sx={{ flex: { xs: "1 1 auto", sm: "0 0 auto" } }}
              >
                Info & FAQ
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenKnockoutPreview}
                size="small"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Generate Knockout
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.open(`/cupOfNations/${nationsLeagueId}`, "_blank")}
                size="small"
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                View Public
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {!groupsDrawn && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6">Set up the edition</Typography>
              <Typography variant="body2" color="text.secondary">
                Check the nations and add their players, then draw the groups.
                Click a nation to edit it, add players or remove it.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={teams.length >= 12}
                onClick={() => {
                  setTeamFormName("");
                  setTeamFormFlag("");
                  setAddNationOpen(true);
                }}
              >
                Add nation
              </Button>
              <Button variant="outlined" disabled={hasByeTeam || teams.length >= 12 || teamBusy} onClick={handleAddBye}>
                Add BYE
              </Button>
              <Button
                variant="contained"
                disabled={!teamCountValid || (hasByeTeam && teams.length !== 12)}
                onClick={openDrawDialog}
              >
                Draw groups
              </Button>
            </Box>
          </Box>
          {(!teamCountValid || (hasByeTeam && teams.length !== 12)) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {hasByeTeam && teams.length !== 12
                ? "A BYE is only supported with 12 teams."
                : `${teams.length} teams so far - you need 8 or 12 to draw groups (a BYE counts as a team).`}
            </Alert>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            {teams.map((team) => {
              const playerCount = team.players?.length || 0;
              return (
                <Card
                  key={team.id}
                  variant="outlined"
                  sx={{ borderStyle: team.isBye ? "dashed" : "solid", opacity: team.isBye ? 0.8 : 1 }}
                >
                  <Box sx={{ position: "relative", height: "100%" }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => !team.isBye && handleOpenRosterDialog(team)}
                      sx={{
                        all: "unset",
                        boxSizing: "border-box",
                        width: "100%",
                        height: "100%",
                        p: 2,
                        textAlign: "center",
                        cursor: team.isBye ? "default" : "pointer",
                        "&:hover": team.isBye ? undefined : { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography sx={{ fontSize: 36, lineHeight: 1.2 }}>{getFlagEmoji(team.flag)}</Typography>
                      <Typography fontWeight="bold">{team.name}</Typography>
                      <Typography variant="caption" color={playerCount === 0 && !team.isBye ? "warning.main" : "text.secondary"}>
                        {team.isBye
                          ? "Placeholder - no matches"
                          : playerCount === 0
                            ? "No players yet"
                            : `${playerCount} player${playerCount === 1 ? "" : "s"}`}
                      </Typography>
                    </Box>
                    {team.isBye && (
                      <IconButton
                        size="small"
                        aria-label="Remove BYE"
                        sx={{ position: "absolute", top: 4, right: 4 }}
                        onClick={() => handleDeleteTeam(team)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Paper>
      )}

      {teams.length > 0 && (
        <>
          <Paper sx={{ mb: 3 }}>
            <Box
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
                "&::-webkit-scrollbar": {
                  height: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                },
              }}
            >
              <Tabs
                value={currentTab}
                onChange={(_, v) => setCurrentTab(v)}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTab-root": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    minWidth: { xs: 70, sm: 120 },
                    px: { xs: 0.5, sm: 2 },
                  },
                  "& .MuiTabs-scrollButtons": {
                    "&.Mui-disabled": { opacity: 0.3 },
                  },
                }}
              >
                <Tab label="Group A" />
                <Tab label="Group B" />
                {hasGroupC && <Tab label="Group C" />}
                <Tab label="Semifinals" />
                <Tab label="3rd/7th Place" />
                <Tab label="Finals" />
              </Tabs>
            </Box>
          </Paper>

          <Box sx={{ mt: 3 }}>
            {/* Render country flags for current tab */}
            {renderCountryFlags()}

            {currentTab === 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: "1rem", md: "1.25rem" } }}>
                  Group A Standings
                </Typography>
                {renderStandingsTable(standingsA)}
                <Typography variant="h6" sx={{ mb: 2, mt: 3, fontSize: { xs: "1rem", md: "1.25rem" } }}>
                  Group A Matches
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(getMatchesForTab()) : renderMatchesTable(getMatchesForTab())}
              </>
            )}

            {currentTab === 1 && (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: "1rem", md: "1.25rem" } }}>
                  Group B Standings
                </Typography>
                {renderStandingsTable(standingsB)}
                <Typography variant="h6" sx={{ mb: 2, mt: 3, fontSize: { xs: "1rem", md: "1.25rem" } }}>
                  Group B Matches
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(getMatchesForTab()) : renderMatchesTable(getMatchesForTab())}
              </>
            )}

            {hasGroupC && currentTab === 2 && (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: "1rem", md: "1.25rem" } }}>
                  Group C Standings
                </Typography>
                {renderStandingsTable(standingsC)}
                <Typography variant="h6" sx={{ mb: 2, mt: 3, fontSize: { xs: "1rem", md: "1.25rem" } }}>
                  Group C Matches
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(getMatchesForTab()) : renderMatchesTable(getMatchesForTab())}
              </>
            )}

            {currentTab === koBase && (
              <>
                {/* Semifinals - Top Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 1, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                  Semifinals - Top Bracket (Positions 1-4)
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(
                  matches.filter((m) => m.stage === "semifinal_top")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "semifinal_top")
                )}

                {/* Semifinals - Bottom Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 4, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                  Semifinals - Bottom Bracket (Positions 5-8)
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(
                  matches.filter((m) => m.stage === "semifinal_bottom")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "semifinal_bottom")
                )}
              </>
            )}

            {currentTab === koBase + 1 && (
              <>
                {/* 3rd Place Match - Top Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 1, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                  3rd Place Match (Positions 3-4)
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(
                  matches.filter((m) => m.stage === "third_place_top")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "third_place_top")
                )}

                {/* 7th Place Match - Bottom Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 4, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                  7th Place Match (Positions 7-8)
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(
                  matches.filter((m) => m.stage === "third_place_bottom")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "third_place_bottom")
                )}
              </>
            )}

            {currentTab === koBase + 2 && (
              <>
                {/* Final - Top Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 1, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                  Final - Top Bracket (Positions 1-2)
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(
                  matches.filter((m) => m.stage === "final_top")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "final_top")
                )}

                {/* Final - Bottom Bracket */}
                <Typography variant="h6" sx={{ mb: 2, mt: 4, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                  Final - Bottom Bracket (Positions 5-6)
                </Typography>
                {isMobile ? renderMatchesAsMobileCards(
                  matches.filter((m) => m.stage === "final_bottom")
                ) : renderMatchesTable(
                  matches.filter((m) => m.stage === "final_bottom")
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* Score Dialog */}
      <Dialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Edit Matchup Scores
          {selectedMatch && (
            <Typography variant="body2" color="text.secondary">
              {getFlagEmoji(selectedMatch.team1Flag)} {selectedMatch.team1Name}{" "}
              vs {selectedMatch.team2Name}{" "}
              {getFlagEmoji(selectedMatch.team2Flag)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={selectedIndividualMatch - 1}
              onChange={(_, newValue) =>
                setSelectedIndividualMatch((newValue + 1) as 1 | 2 | 3)
              }
            >
              <Tab label="Match 1" />
              <Tab label="Match 2" />
              <Tab label="Match 3" />
            </Tabs>
          </Box>

          {/* Match 1 */}
          {selectedIndividualMatch === 1 && (
            <Box>
              {/* Did Not Compete Alert */}
              {(match1Team1DidNotCompete || match1Team2DidNotCompete) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {match1Team1DidNotCompete && match1Team2DidNotCompete ? (
                    <Typography variant="body2">
                      <strong>Both teams did not compete.</strong> This match
                      will not count towards standings.
                    </Typography>
                  ) : match1Team1DidNotCompete ? (
                    <Typography variant="body2">
                      <strong>
                        {selectedMatch?.team1Name} did not compete.
                      </strong>{" "}
                      Score automatically set to 6-0, 6-0 in favor of{" "}
                      {selectedMatch?.team2Name}.
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      <strong>
                        {selectedMatch?.team2Name} did not compete.
                      </strong>{" "}
                      Score automatically set to 6-0, 6-0 in favor of{" "}
                      {selectedMatch?.team1Name}.
                    </Typography>
                  )}
                </Alert>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Set 1 (First to 6)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                  label={selectedMatch?.team1Name}
                  type="number"
                  value={match1Set1Team1}
                  onChange={(e) => setMatch1Set1Team1(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match1Team1DidNotCompete || match1Team2DidNotCompete
                  }
                />
                <TextField
                  label={selectedMatch?.team2Name}
                  type="number"
                  value={match1Set1Team2}
                  onChange={(e) => setMatch1Set1Team2(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match1Team1DidNotCompete || match1Team2DidNotCompete
                  }
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Set 2 (First to 6)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                  label={selectedMatch?.team1Name}
                  type="number"
                  value={match1Set2Team1}
                  onChange={(e) => setMatch1Set2Team1(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match1Team1DidNotCompete || match1Team2DidNotCompete
                  }
                />
                <TextField
                  label={selectedMatch?.team2Name}
                  type="number"
                  value={match1Set2Team2}
                  onChange={(e) => setMatch1Set2Team2(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match1Team1DidNotCompete || match1Team2DidNotCompete
                  }
                />
              </Box>

              {shouldShowMatch1Set3() && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Set 3 (First to 6) - Deciding Set
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label={selectedMatch?.team1Name}
                      type="number"
                      value={match1Set3Team1}
                      onChange={(e) => setMatch1Set3Team1(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match1Team1DidNotCompete || match1Team2DidNotCompete
                      }
                    />
                    <TextField
                      label={selectedMatch?.team2Name}
                      type="number"
                      value={match1Set3Team2}
                      onChange={(e) => setMatch1Set3Team2(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match1Team1DidNotCompete || match1Team2DidNotCompete
                      }
                    />
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Match 2 */}
          {selectedIndividualMatch === 2 && (
            <Box>
              {/* Did Not Compete Alert */}
              {(match2Team1DidNotCompete || match2Team2DidNotCompete) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {match2Team1DidNotCompete && match2Team2DidNotCompete ? (
                    <Typography variant="body2">
                      <strong>Both teams did not compete.</strong> This match
                      will not count towards standings.
                    </Typography>
                  ) : match2Team1DidNotCompete ? (
                    <Typography variant="body2">
                      <strong>
                        {selectedMatch?.team1Name} did not compete.
                      </strong>{" "}
                      Score automatically set to 6-0, 6-0 in favor of{" "}
                      {selectedMatch?.team2Name}.
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      <strong>
                        {selectedMatch?.team2Name} did not compete.
                      </strong>{" "}
                      Score automatically set to 6-0, 6-0 in favor of{" "}
                      {selectedMatch?.team1Name}.
                    </Typography>
                  )}
                </Alert>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Set 1 (First to 6)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                  label={selectedMatch?.team1Name}
                  type="number"
                  value={match2Set1Team1}
                  onChange={(e) => setMatch2Set1Team1(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match2Team1DidNotCompete || match2Team2DidNotCompete
                  }
                />
                <TextField
                  label={selectedMatch?.team2Name}
                  type="number"
                  value={match2Set1Team2}
                  onChange={(e) => setMatch2Set1Team2(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match2Team1DidNotCompete || match2Team2DidNotCompete
                  }
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Set 2 (First to 6)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <TextField
                  label={selectedMatch?.team1Name}
                  type="number"
                  value={match2Set2Team1}
                  onChange={(e) => setMatch2Set2Team1(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match2Team1DidNotCompete || match2Team2DidNotCompete
                  }
                />
                <TextField
                  label={selectedMatch?.team2Name}
                  type="number"
                  value={match2Set2Team2}
                  onChange={(e) => setMatch2Set2Team2(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  disabled={
                    match2Team1DidNotCompete || match2Team2DidNotCompete
                  }
                />
              </Box>

              {shouldShowMatch2Set3() && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Set 3 (First to 6) - Deciding Set
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label={selectedMatch?.team1Name}
                      type="number"
                      value={match2Set3Team1}
                      onChange={(e) => setMatch2Set3Team1(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match2Team1DidNotCompete || match2Team2DidNotCompete
                      }
                    />
                    <TextField
                      label={selectedMatch?.team2Name}
                      type="number"
                      value={match2Set3Team2}
                      onChange={(e) => setMatch2Set3Team2(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match2Team1DidNotCompete || match2Team2DidNotCompete
                      }
                    />
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Match 3 */}
          {selectedIndividualMatch === 3 && (
            <Box>
              {/* Check if this is a super tiebreak match */}
              {selectedMatch?.matches[2]?.isSuperTiebreakDecider && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>🏆 SUPER TIEBREAK:</strong> This is a deciding match played as a single set to 10 points (win by 2).
                    <br />Players are mixed from Match 1 and Match 2.
                  </Typography>
                </Alert>
              )}
              
              {/* Did Not Compete Alert */}
              {(match3Team1DidNotCompete || match3Team2DidNotCompete) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {match3Team1DidNotCompete && match3Team2DidNotCompete ? (
                    <Typography variant="body2">
                      <strong>Both teams did not compete.</strong> This match
                      will not count towards standings.
                    </Typography>
                  ) : match3Team1DidNotCompete ? (
                    <Typography variant="body2">
                      <strong>
                        {selectedMatch?.team1Name} did not compete.
                      </strong>{" "}
                      Score automatically set to {selectedMatch?.matches[2]?.isSuperTiebreakDecider ? '10-0' : '6-0, 6-0'} in favor of{" "}
                      {selectedMatch?.team2Name}.
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      <strong>
                        {selectedMatch?.team2Name} did not compete.
                      </strong>{" "}
                      Score automatically set to {selectedMatch?.matches[2]?.isSuperTiebreakDecider ? '10-0' : '6-0, 6-0'} in favor of{" "}
                      {selectedMatch?.team1Name}.
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Super Tiebreak - Single Set to 10 */}
              {selectedMatch?.matches[2]?.isSuperTiebreakDecider ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Super Tiebreak (First to 10, win by 2)
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                    <TextField
                      label={selectedMatch?.team1Name}
                      type="number"
                      value={match3Set1Team1}
                      onChange={(e) => setMatch3Set1Team1(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match3Team1DidNotCompete || match3Team2DidNotCompete
                      }
                    />
                    <TextField
                      label={selectedMatch?.team2Name}
                      type="number"
                      value={match3Set1Team2}
                      onChange={(e) => setMatch3Set1Team2(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match3Team1DidNotCompete || match3Team2DidNotCompete
                      }
                    />
                  </Box>
                </>
              ) : (
                <>
                  {/* Regular Match - 3 Sets */}
                  <Typography variant="subtitle2" gutterBottom>
                    Set 1 (First to 6)
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                    <TextField
                      label={selectedMatch?.team1Name}
                      type="number"
                      value={match3Set1Team1}
                      onChange={(e) => setMatch3Set1Team1(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match3Team1DidNotCompete || match3Team2DidNotCompete
                      }
                    />
                    <TextField
                      label={selectedMatch?.team2Name}
                      type="number"
                      value={match3Set1Team2}
                      onChange={(e) => setMatch3Set1Team2(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match3Team1DidNotCompete || match3Team2DidNotCompete
                      }
                    />
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Set 2 (First to 6)
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                    <TextField
                      label={selectedMatch?.team1Name}
                      type="number"
                      value={match3Set2Team1}
                      onChange={(e) => setMatch3Set2Team1(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match3Team1DidNotCompete || match3Team2DidNotCompete
                      }
                    />
                    <TextField
                      label={selectedMatch?.team2Name}
                      type="number"
                      value={match3Set2Team2}
                      onChange={(e) => setMatch3Set2Team2(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={
                        match3Team1DidNotCompete || match3Team2DidNotCompete
                      }
                    />
                  </Box>

                  {shouldShowMatch3Set3() && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Set 3 (First to 6) - Deciding Set
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          label={selectedMatch?.team1Name}
                          type="number"
                          value={match3Set3Team1}
                          onChange={(e) => setMatch3Set3Team1(e.target.value)}
                          fullWidth
                          inputProps={{ min: 0 }}
                          disabled={
                            match3Team1DidNotCompete || match3Team2DidNotCompete
                          }
                        />
                        <TextField
                          label={selectedMatch?.team2Name}
                          type="number"
                          value={match3Set3Team2}
                          onChange={(e) => setMatch3Set3Team2(e.target.value)}
                          fullWidth
                          inputProps={{ min: 0 }}
                          disabled={
                            match3Team1DidNotCompete || match3Team2DidNotCompete
                          }
                        />
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button 
            onClick={handleMarkAsDraw} 
            variant="outlined" 
            color="warning"
            sx={{ mr: 1 }}
          >
            Mark as Draw (1pt each)
          </Button>
          <Button onClick={handleSaveScore} variant="contained">
            Save Scores
          </Button>
        </DialogActions>
      </Dialog>

      <NationsLeagueMatchDays
        open={matchDaysOpen}
        onClose={() => setMatchDaysOpen(false)}
        nationsLeagueId={nationsLeagueId!}
        teams={teams}
        matches={matches}
        onMatchesChanged={fetchAllData}
      />

      {/* Roster Dialog */}
      <Dialog
        open={rosterDialogOpen}
        onClose={() => setRosterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {groupsDrawn ? "Manage Team Roster" : "Nation & players"}
          {selectedTeam && groupsDrawn && (
            <Typography variant="body2" color="text.secondary">
              {getFlagEmoji(selectedTeam.flag)} {selectedTeam.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {!groupsDrawn && selectedTeam && !selectedTeam.isBye && (
              <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                <TextField
                  label="Nation name"
                  size="small"
                  fullWidth
                  value={teamFormName}
                  onChange={(e) => setTeamFormName(e.target.value)}
                />
                <TextField
                  label="Flag"
                  size="small"
                  sx={{ width: 150 }}
                  value={teamFormFlag}
                  onChange={(e) => setTeamFormFlag(e.target.value)}
                  placeholder="EG or 🇪🇬"
                  InputProps={{
                    startAdornment: teamFormFlag ? (
                      <Box component="span" sx={{ mr: 1, fontSize: 20 }}>
                        {getFlagEmoji(teamFormFlag)}
                      </Box>
                    ) : undefined,
                  }}
                />
              </Box>
            )}
            {selectedTeam && !selectedTeam.isBye && (
              <Box sx={{ mb: 2 }}>
                {selectedTeam.rosterLocked ? (
                  <>
                    <Alert severity={rosterLimitReached && !rosterBypass ? "warning" : "info"} sx={{ mb: 1 }}>
                      Final roster locked. Changes used — Group: {selectedTeam.rosterChanges?.group ?? 0}/1 · Knockout: {selectedTeam.rosterChanges?.knockout ?? 0}/1
                      {rosterLimitReached && ` (${currentRosterPhase} stage change already used)`}
                    </Alert>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={rosterBypass}
                          onChange={(e) => setRosterBypass(e.target.checked)}
                          color="warning"
                        />
                      }
                      label="Admin override (emergency change, doesn't use the team's change)"
                    />
                    {rosterBypass && (
                      <TextField
                        label="Reason (required)"
                        size="small"
                        fullWidth
                        value={rosterBypassReason}
                        onChange={(e) => setRosterBypassReason(e.target.value)}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </>
                ) : (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={confirmFinalRoster}
                        onChange={(e) => setConfirmFinalRoster(e.target.checked)}
                      />
                    }
                    label="This is the team's final initial roster: players + power ranking (any later change counts)"
                  />
                )}
              </Box>
            )}
            <Typography variant="subtitle2" gutterBottom>
              Current Roster ({rosterPlayers.length} players)
              <Chip label="Drag to reorder" size="small" sx={{ ml: 1 }} />
            </Typography>
            <List sx={{ mb: 2, maxHeight: 300, overflow: "auto" }}>
              {rosterPlayers.length === 0 ? (
                <ListItem>
                  <ListItemText secondary="No players added yet" />
                </ListItem>
              ) : (
                rosterPlayers.map((player, index) => (
                  <ListItem
                    key={index}
                    draggable={editingIndex !== index}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      cursor: editingIndex === index ? "default" : "move",
                      backgroundColor:
                        draggedIndex === index ? "action.hover" : "transparent",
                      "&:hover": { backgroundColor: "action.hover" },
                      transition: "background-color 0.2s",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 1,
                    }}
                    secondaryAction={
                      editingIndex === index ? (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            edge="end"
                            onClick={handleSaveEdit}
                            color="primary"
                            size="small"
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={handleCancelEdit}
                            size="small"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            edge="end"
                            onClick={() => handleStartEdit(index)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemovePlayer(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )
                    }
                  >
                    {editingIndex === index ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <DragIndicatorIcon sx={{ color: "action.disabled" }} />
                        <Box sx={{ position: "relative" }}>
                          <SafeAvatar
                            src={editingImagePreview}
                            alt={editingName}
                            fallbackText={editingName}
                            sx={{ width: 40, height: 40 }}
                          />
                          <IconButton
                            component="label"
                            size="small"
                            sx={{
                              position: "absolute",
                              bottom: -5,
                              right: -5,
                              backgroundColor: "background.paper",
                              "&:hover": { backgroundColor: "action.hover" },
                            }}
                          >
                            <PhotoCameraIcon fontSize="small" />
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={(e) => handlePlayerImageChange(e, true)}
                            />
                          </IconButton>
                        </Box>
                        <TextField
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onPaste={handlePasteInEdit}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleSaveEdit();
                            } else if (e.key === "Escape") {
                              handleCancelEdit();
                            }
                          }}
                          size="small"
                          fullWidth
                          autoFocus
                          label={`Rank ${index + 1}`}
                          helperText="Paste text for name or image for photo"
                        />
                      </Box>
                    ) : (
                      <>
                        <DragIndicatorIcon
                          sx={{ mr: 1, color: "action.active" }}
                        />
                        <ListItemAvatar>
                          <SafeAvatar
                            src={player.profilePicture}
                            alt={player.name}
                            fallbackText={player.name}
                            sx={{ width: 40, height: 40 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${index + 1}. ${player.name}`}
                          secondary={
                            <>
                              {`Rank ${index + 1}`}
                              {player.playerId && (
                                <Chip label="SPL account" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                              )}
                            </>
                          }
                          secondaryTypographyProps={{ component: "div" }}
                        />
                      </>
                    )}
                  </ListItem>
                ))
              )}
            </List>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption">
                <strong>Player Rankings:</strong> Order determines player rank
                (1st = highest rank).
                <br />• <strong>Drag</strong> players to reorder their rank
                <br />• <strong>Edit icon</strong> to change a player's name or
                photo
                <br />• <strong>Delete icon</strong> to remove a player
                <br />
                Rankings are used for match assignments where higher-ranked
                players play in earlier matches.
              </Typography>
            </Alert>

            <Typography variant="subtitle2" gutterBottom>
              Add New Player
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              {newPlayerImagePreview ? (
                <Box sx={{ position: "relative" }}>
                  <SafeAvatar
                    src={newPlayerImagePreview}
                    alt="New player"
                    fallbackText={newPlayerName || "?"}
                    sx={{ width: 56, height: 56 }}
                  />
                  <IconButton
                    component="label"
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: -5,
                      right: -5,
                      backgroundColor: "background.paper",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <PhotoCameraIcon fontSize="small" />
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={(e) => handlePlayerImageChange(e, false)}
                    />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  sx={{ minHeight: 56 }}
                >
                  Photo
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={(e) => handlePlayerImageChange(e, false)}
                  />
                </Button>
              )}
              <Autocomplete
                freeSolo
                fullWidth
                size="small"
                options={splPlayers.filter(
                  (p) => !rosterPlayers.some((r) => r.playerId === p.id),
                )}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.name)}
                inputValue={newPlayerName}
                value={selectedSplPlayer}
                onInputChange={(_, value, reason) => {
                  setNewPlayerName(value);
                  // Typing (or clearing) detaches from the picked SPL player
                  if (reason !== "reset") {
                    setSelectedSplPlayer(null);
                  }
                }}
                onChange={(_, value) => {
                  if (value && typeof value !== "string") {
                    setSelectedSplPlayer(value);
                    if (value.profilePicture) {
                      setNewPlayerImage(null);
                      setNewPlayerImagePreview(value.profilePicture);
                    }
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <SafeAvatar
                      src={option.profilePicture}
                      alt={option.name}
                      fallbackText={option.name}
                      sx={{ width: 28, height: 28, mr: 1 }}
                    />
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.division}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Player Name"
                    onPaste={handlePasteInNewPlayer}
                    helperText={
                      selectedSplPlayer
                        ? "Linked to SPL player account"
                        : "Search SPL players, or type a name for a player without an account"
                    }
                  />
                )}
              />
              <Button
                variant="contained"
                onClick={handleAddPlayer}
                disabled={!newPlayerName.trim() || uploadingImage}
                startIcon={
                  uploadingImage ? <CircularProgress size={20} /> : <AddIcon />
                }
              >
                Add
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {!groupsDrawn && selectedTeam && (
            <Button
              color="error"
              onClick={() => handleDeleteTeam(selectedTeam)}
              disabled={teamBusy}
              sx={{ mr: "auto" }}
            >
              Remove nation
            </Button>
          )}
          <Button onClick={() => setRosterDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveRoster}
            variant="contained"
            disabled={
              !!selectedTeam?.rosterLocked &&
              ((rosterBypass && !rosterBypassReason.trim()) ||
                (!rosterBypass && rosterLimitReached))
            }
          >
            {groupsDrawn ? "Save Roster" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date/Time Dialog */}
      <Dialog
        open={dateTimeDialogOpen}
        onClose={() => setDateTimeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Set Match Date & Time
          {selectedMatchForDateTime && (
            <Typography variant="body2" color="text.secondary">
              {getFlagEmoji(selectedMatchForDateTime.team1Flag)}{" "}
              {selectedMatchForDateTime.team1Name} vs{" "}
              {selectedMatchForDateTime.team2Name}{" "}
              {getFlagEmoji(selectedMatchForDateTime.team2Flag)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Match Date & Time"
              type="datetime-local"
              value={matchDateTime}
              onChange={(e) => setMatchDateTime(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to show 'Date to be announced soon' on public page"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateTimeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setMatchDateTime("");
            }}
            color="secondary"
          >
            Clear Date
          </Button>
          <Button onClick={handleSaveDateTime} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player Assignment Dialog */}
      <Dialog
        open={playerDialogOpen}
        onClose={() => setPlayerDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Assign Players to Matches
          {selectedMatchForPlayers && (
            <Typography variant="body2" color="text.secondary">
              {getFlagEmoji(selectedMatchForPlayers.team1Flag)}{" "}
              {selectedMatchForPlayers.team1Name} vs{" "}
              {selectedMatchForPlayers.team2Name}{" "}
              {getFlagEmoji(selectedMatchForPlayers.team2Flag)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedMatchForPlayers && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Sequential Pairing Rules:</strong>
                  <br />• <strong>Match 1:</strong> Select any 2 players from
                  each team (typically highest ranked)
                  <br />• <strong>Match 2:</strong> Only players ranked LOWER
                  than Match 1's lowest-ranked player are available
                  <br />• <strong>Match 3:</strong> No restriction, any player
                  can be entered
                  <br />
                  <br />
                  <em>
                    This enforces consecutive pairing by rank. Example: If Match
                    1 has Rank 1 & 3, then Match 2 can only select from Rank 4
                    and below.
                  </em>
                  <br />
                  <strong>Tip:</strong> You can enter one nation's players
                  before the other's. Scores can only be entered once both
                  nations are listed.
                  <br />
                  <strong>Note:</strong> You cannot select the same player twice
                  within one match.
                </Typography>
              </Alert>

              {/* Match 1 */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Match 1 - Highest Ranked Pair
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getFlagEmoji(selectedMatchForPlayers.team1Flag)}{" "}
                      {selectedMatchForPlayers.team1Name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={match1Team1DidNotCompete}
                          onChange={(e) => {
                            setMatch1Team1DidNotCompete(e.target.checked);
                            if (e.target.checked) {
                              setMatch1Team1Player1("");
                              setMatch1Team1Player2("");
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Did Not Compete"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      select
                      label="Player 1"
                      value={match1Team1Player1}
                      onChange={(e) => setMatch1Team1Player1(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mb: 1 }}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match1Team1DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team1Id,
                        true,
                        1,
                        1,
                        match1Team1Player1,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team1Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                    <TextField
                      select
                      label="Player 2"
                      value={match1Team1Player2}
                      onChange={(e) => setMatch1Team1Player2(e.target.value)}
                      fullWidth
                      size="small"
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match1Team1DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team1Id,
                        true,
                        1,
                        2,
                        match1Team1Player2,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team1Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getFlagEmoji(selectedMatchForPlayers.team2Flag)}{" "}
                      {selectedMatchForPlayers.team2Name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={match1Team2DidNotCompete}
                          onChange={(e) => {
                            setMatch1Team2DidNotCompete(e.target.checked);
                            if (e.target.checked) {
                              setMatch1Team2Player1("");
                              setMatch1Team2Player2("");
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Did Not Compete"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      select
                      label="Player 1"
                      value={match1Team2Player1}
                      onChange={(e) => setMatch1Team2Player1(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mb: 1 }}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match1Team2DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team2Id,
                        false,
                        1,
                        1,
                        match1Team2Player1,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team2Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                    <TextField
                      select
                      label="Player 2"
                      value={match1Team2Player2}
                      onChange={(e) => setMatch1Team2Player2(e.target.value)}
                      fullWidth
                      size="small"
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match1Team2DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team2Id,
                        false,
                        1,
                        2,
                        match1Team2Player2,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team2Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                  </Box>
                </Box>
              </Paper>

              {/* Match 2 */}
              <Paper
                sx={{ p: 2, mb: 2 }}
              >
                <Typography variant="h6" gutterBottom>
                  Match 2 - Next Available Pair
                  {!isSideComplete(1, true) && !isSideComplete(1, false) && (
                    <Chip
                      label="Complete Match 1 First"
                      size="small"
                      sx={{ ml: 2 }}
                      color="warning"
                    />
                  )}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getFlagEmoji(selectedMatchForPlayers.team1Flag)}{" "}
                      {selectedMatchForPlayers.team1Name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={match2Team1DidNotCompete}
                          onChange={(e) => {
                            setMatch2Team1DidNotCompete(e.target.checked);
                            if (e.target.checked) {
                              setMatch2Team1Player1("");
                              setMatch2Team1Player2("");
                            }
                          }}
                          size="small"
                          disabled={!isSideComplete(1, true)}
                        />
                      }
                      label="Did Not Compete"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      select
                      label="Player 1"
                      value={match2Team1Player1}
                      onChange={(e) => setMatch2Team1Player1(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mb: 1 }}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isSideComplete(1, true) || match2Team1DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team1Id,
                        true,
                        2,
                        1,
                        match2Team1Player1,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team1Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                    <TextField
                      select
                      label="Player 2"
                      value={match2Team1Player2}
                      onChange={(e) => setMatch2Team1Player2(e.target.value)}
                      fullWidth
                      size="small"
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isSideComplete(1, true) || match2Team1DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team1Id,
                        true,
                        2,
                        2,
                        match2Team1Player2,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team1Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getFlagEmoji(selectedMatchForPlayers.team2Flag)}{" "}
                      {selectedMatchForPlayers.team2Name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={match2Team2DidNotCompete}
                          onChange={(e) => {
                            setMatch2Team2DidNotCompete(e.target.checked);
                            if (e.target.checked) {
                              setMatch2Team2Player1("");
                              setMatch2Team2Player2("");
                            }
                          }}
                          size="small"
                          disabled={!isSideComplete(1, false)}
                        />
                      }
                      label="Did Not Compete"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      select
                      label="Player 1"
                      value={match2Team2Player1}
                      onChange={(e) => setMatch2Team2Player1(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mb: 1 }}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isSideComplete(1, false) || match2Team2DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team2Id,
                        false,
                        2,
                        1,
                        match2Team2Player1,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team2Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                    <TextField
                      select
                      label="Player 2"
                      value={match2Team2Player2}
                      onChange={(e) => setMatch2Team2Player2(e.target.value)}
                      fullWidth
                      size="small"
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isSideComplete(1, false) || match2Team2DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {getAvailablePlayersForPosition(
                        selectedMatchForPlayers.team2Id,
                        false,
                        2,
                        2,
                        match2Team2Player2,
                      ).map((player, idx) => {
                        const roster = getTeamRoster(
                          selectedMatchForPlayers.team2Id,
                        );
                        const rank = roster.indexOf(player) + 1;
                        return (
                          <option key={idx} value={player}>
                            {player} (Rank {rank})
                          </option>
                        );
                      })}
                    </TextField>
                  </Box>
                </Box>
              </Paper>

              {/* Match 3 */}
              <Paper
                sx={{ p: 2, mb: 2 }}
              >
                <Typography variant="h6" gutterBottom>
                  {isSuperTiebreakScenario() 
                    ? "Match 3 - 🏆 SUPER TIEBREAK (to 10 points)" 
                    : "Match 3 - Remaining Players"}
                </Typography>
                
                {isSuperTiebreakScenario() && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Super Tiebreak Mode:</strong>
                      <br />• Matches are tied 1-1, so Match 3 is a Super Tiebreak to 10 points
                      <br />• Any player from each team's roster can be entered
                    </Typography>
                  </Alert>
                )}
                
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getFlagEmoji(selectedMatchForPlayers.team1Flag)}{" "}
                      {selectedMatchForPlayers.team1Name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={match3Team1DidNotCompete}
                          onChange={(e) => {
                            setMatch3Team1DidNotCompete(e.target.checked);
                            if (e.target.checked) {
                              setMatch3Team1Player1("");
                              setMatch3Team1Player2("");
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Did Not Compete"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      select
                      label="Player 1"
                      value={match3Team1Player1}
                      onChange={(e) => setMatch3Team1Player1(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mb: 1 }}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match3Team1DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {(isSuperTiebreakScenario() 
                        ? getAvailablePlayersForSuperTiebreak(true, 1, match3Team1Player1)
                        : getAvailablePlayersForPosition(
                            selectedMatchForPlayers.team1Id,
                            true,
                            3,
                            1,
                            match3Team1Player1,
                          )
                      ).map((player, idx) => {
                        if (isSuperTiebreakScenario()) {
                          // Show which match the player is from
                          const fromMatch1 = player === match1Team1Player1 || player === match1Team1Player2;
                          const fromMatch2 = player === match2Team1Player1 || player === match2Team1Player2;
                          const matchLabel = fromMatch1 ? " (Match 1)" : fromMatch2 ? " (Match 2)" : "";
                          return (
                            <option key={idx} value={player}>
                              {player}{matchLabel}
                            </option>
                          );
                        } else {
                          const roster = getTeamRoster(
                            selectedMatchForPlayers.team1Id,
                          );
                          const rank = roster.indexOf(player) + 1;
                          return (
                            <option key={idx} value={player}>
                              {player} (Rank {rank})
                            </option>
                          );
                        }
                      })}
                    </TextField>
                    <TextField
                      select
                      label="Player 2"
                      value={match3Team1Player2}
                      onChange={(e) => setMatch3Team1Player2(e.target.value)}
                      fullWidth
                      size="small"
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match3Team1DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {(isSuperTiebreakScenario() 
                        ? getAvailablePlayersForSuperTiebreak(true, 2, match3Team1Player2)
                        : getAvailablePlayersForPosition(
                            selectedMatchForPlayers.team1Id,
                            true,
                            3,
                            2,
                            match3Team1Player2,
                          )
                      ).map((player, idx) => {
                        if (isSuperTiebreakScenario()) {
                          // Show which match the player is from
                          const fromMatch1 = player === match1Team1Player1 || player === match1Team1Player2;
                          const fromMatch2 = player === match2Team1Player1 || player === match2Team1Player2;
                          const matchLabel = fromMatch1 ? " (Match 1)" : fromMatch2 ? " (Match 2)" : "";
                          return (
                            <option key={idx} value={player}>
                              {player}{matchLabel}
                            </option>
                          );
                        } else {
                          const roster = getTeamRoster(
                            selectedMatchForPlayers.team1Id,
                          );
                          const rank = roster.indexOf(player) + 1;
                          return (
                            <option key={idx} value={player}>
                              {player} (Rank {rank})
                            </option>
                          );
                        }
                      })}
                    </TextField>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {getFlagEmoji(selectedMatchForPlayers.team2Flag)}{" "}
                      {selectedMatchForPlayers.team2Name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={match3Team2DidNotCompete}
                          onChange={(e) => {
                            setMatch3Team2DidNotCompete(e.target.checked);
                            if (e.target.checked) {
                              setMatch3Team2Player1("");
                              setMatch3Team2Player2("");
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Did Not Compete"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      select
                      label="Player 1"
                      value={match3Team2Player1}
                      onChange={(e) => setMatch3Team2Player1(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mb: 1 }}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match3Team2DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {(isSuperTiebreakScenario() 
                        ? getAvailablePlayersForSuperTiebreak(false, 1, match3Team2Player1)
                        : getAvailablePlayersForPosition(
                            selectedMatchForPlayers.team2Id,
                            false,
                            3,
                            1,
                            match3Team2Player1,
                          )
                      ).map((player, idx) => {
                        if (isSuperTiebreakScenario()) {
                          // Show which match the player is from
                          const fromMatch1 = player === match1Team2Player1 || player === match1Team2Player2;
                          const fromMatch2 = player === match2Team2Player1 || player === match2Team2Player2;
                          const matchLabel = fromMatch1 ? " (Match 1)" : fromMatch2 ? " (Match 2)" : "";
                          return (
                            <option key={idx} value={player}>
                              {player}{matchLabel}
                            </option>
                          );
                        } else {
                          const roster = getTeamRoster(
                            selectedMatchForPlayers.team2Id,
                          );
                          const rank = roster.indexOf(player) + 1;
                          return (
                            <option key={idx} value={player}>
                              {player} (Rank {rank})
                            </option>
                          );
                        }
                      })}
                    </TextField>
                    <TextField
                      select
                      label="Player 2"
                      value={match3Team2Player2}
                      onChange={(e) => setMatch3Team2Player2(e.target.value)}
                      fullWidth
                      size="small"
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      disabled={match3Team2DidNotCompete}
                    >
                      <option value="">Select Player</option>
                      {(isSuperTiebreakScenario() 
                        ? getAvailablePlayersForSuperTiebreak(false, 2, match3Team2Player2)
                        : getAvailablePlayersForPosition(
                            selectedMatchForPlayers.team2Id,
                            false,
                            3,
                            2,
                            match3Team2Player2,
                          )
                      ).map((player, idx) => {
                        if (isSuperTiebreakScenario()) {
                          // Show which match the player is from
                          const fromMatch1 = player === match1Team2Player1 || player === match1Team2Player2;
                          const fromMatch2 = player === match2Team2Player1 || player === match2Team2Player2;
                          const matchLabel = fromMatch1 ? " (Match 1)" : fromMatch2 ? " (Match 2)" : "";
                          return (
                            <option key={idx} value={player}>
                              {player}{matchLabel}
                            </option>
                          );
                        } else {
                          const roster = getTeamRoster(
                            selectedMatchForPlayers.team2Id,
                          );
                          const rank = roster.indexOf(player) + 1;
                          return (
                            <option key={idx} value={player}>
                              {player} (Rank {rank})
                            </option>
                          );
                        }
                      })}
                    </TextField>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleClearAllPlayers}
            color="warning"
            variant="outlined"
          >
            Clear All Players
          </Button>
          <Button onClick={handleSavePlayerAssignments} variant="contained">
            Save Players
          </Button>
        </DialogActions>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog
        open={announcementDialogOpen}
        onClose={() => setAnnouncementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CampaignIcon sx={{ mr: 1 }} />
            Create Announcement
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Announcement Message"
              value={announcementMessage}
              onChange={(e) => setAnnouncementMessage(e.target.value)}
              placeholder="Enter your announcement message here..."
              sx={{ mb: 3 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={announcementEnabled}
                  onChange={(e) => setAnnouncementEnabled(e.target.checked)}
                />
              }
              label="Enable Announcement"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnouncementDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSaveAnnouncement();
              setAnnouncementDialogOpen(false);
            }}
            variant="contained"
            disabled={announcementEnabled && !announcementMessage.trim()}
          >
            Save Announcement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Nation Dialog */}
      <Dialog open={addNationOpen} onClose={() => setAddNationOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add nation</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Nation name"
              autoFocus
              value={teamFormName}
              onChange={(e) => setTeamFormName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddNation()}
            />
            <TextField
              label="Flag (country code or emoji)"
              placeholder="EG or 🇪🇬"
              value={teamFormFlag}
              onChange={(e) => setTeamFormFlag(e.target.value)}
              helperText="Optional - leave empty for a neutral flag"
              InputProps={{
                startAdornment: teamFormFlag ? (
                  <Box component="span" sx={{ mr: 1, fontSize: 20 }}>
                    {getFlagEmoji(teamFormFlag)}
                  </Box>
                ) : undefined,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNationOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNation} disabled={!teamFormName.trim() || teamBusy}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Draw Groups Dialog */}
      <Dialog
        open={drawDialogOpen}
        onClose={() => drawStep === "pots" && setDrawDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={drawStep === "animation"}
      >
        {drawStep === "pots" && <DialogTitle>Group draw - set the pots</DialogTitle>}
        <DialogContent sx={drawStep === "animation" ? { p: 0 } : undefined}>
          {drawStep === "pots" && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Put the strongest nations in Pot 1 and the weakest in Pot 4. Each group will get one team
                from each pot. Every pot needs exactly {drawGroupLetters.length} teams.
              </Alert>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
                {pots.map((pot, potIdx) => (
                  <Card variant="outlined" key={potIdx}>
                    <CardContent>
                      <Typography variant="h6" color={pot.length === drawGroupLetters.length ? "primary" : "error"}>
                        Pot {potIdx + 1}{" "}
                        <Typography component="span" variant="caption">
                          ({pot.length}/{drawGroupLetters.length})
                        </Typography>
                      </Typography>
                      <Divider sx={{ mb: 1.5 }} />
                      {pot.map((team) => (
                        <Box
                          key={team.id}
                          sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1, mb: 1 }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {getFlagEmoji(team.flag)} {team.name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                            {potIdx > 0 && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => moveTeamBetweenPots(team.id, potIdx, potIdx - 1)}
                              >
                                ↑ Pot {potIdx}
                              </Button>
                            )}
                            {potIdx < pots.length - 1 && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => moveTeamBetweenPots(team.id, potIdx, potIdx + 1)}
                              >
                                ↓ Pot {potIdx + 2}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </>
          )}

          {drawStep === "animation" && (
            <GroupDrawShow
              key={drawRun}
              title={editionName || "Nations League"}
              plan={drawPlan}
              groupLetters={drawGroupLetters}
              potCount={4}
              getFlag={getFlagEmoji}
              confirming={drawing}
              onConfirm={handleConfirmDraw}
              onRedraw={startDrawAnimation}
              onBack={() => setDrawStep("pots")}
              onClose={() => setDrawDialogOpen(false)}
            />
          )}
        </DialogContent>
        {drawStep === "pots" && (
          <DialogActions>
            <Button onClick={() => setDrawDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={startDrawAnimation} disabled={!potsValid}>
              Start draw animation
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Edition Info & FAQ Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edition Info & FAQ</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="General description"
              value={infoDescription}
              onChange={(e) => setInfoDescription(e.target.value)}
              placeholder="Describe this edition (format, rules, schedule...)"
              sx={{ mb: 3 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Questions & Answers
            </Typography>
            {infoFaq.map((item, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Category (optional)"
                      placeholder="e.g. Roster"
                      value={item.category || ""}
                      onChange={(e) =>
                        setInfoFaq((prev) =>
                          prev.map((f, i) => (i === idx ? { ...f, category: e.target.value } : f)),
                        )
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label={`Question ${idx + 1}`}
                      value={item.question}
                      onChange={(e) =>
                        setInfoFaq((prev) =>
                          prev.map((f, i) => (i === idx ? { ...f, question: e.target.value } : f)),
                        )
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      label="Answer"
                      value={item.answer}
                      onChange={(e) =>
                        setInfoFaq((prev) =>
                          prev.map((f, i) => (i === idx ? { ...f, answer: e.target.value } : f)),
                        )
                      }
                    />
                  </Box>
                  <IconButton
                    aria-label="Remove question"
                    onClick={() => setInfoFaq((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => setInfoFaq((prev) => [...prev, { category: prev.length ? prev[prev.length - 1].category || "" : "", question: "", answer: "" }])}
            >
              Add question
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEditionInfo} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Knockout Preview Dialog */}
      <Dialog
        open={knockoutPreviewOpen}
        onClose={() => setKnockoutPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            🏆 Generate Knockout Stage Matches
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preview of matches that will be created based on current group
            standings
          </Typography>
        </DialogTitle>
        <DialogContent>
          {knockoutPreview && (
            <Box sx={{ pt: 2 }}>
              {/* Top Semifinals */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "success.light" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "success.dark" }}
                >
                  Top Semifinals
                </Typography>
                {knockoutPreview.topSemifinals?.map(
                  (match: any, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {match.description}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {getFlagEmoji(match.team1.flag)} {match.team1.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {match.team1.position}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          vs
                        </Typography>
                        <Box sx={{ flex: 1, textAlign: "right" }}>
                          <Typography variant="body2">
                            {match.team2.name} {getFlagEmoji(match.team2.flag)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {match.team2.position}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ),
                )}
              </Paper>

              {/* Top Final */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "warning.light" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "warning.dark" }}
                >
                  Top Final
                </Typography>
                <Box
                  sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {knockoutPreview.topFinal?.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {knockoutPreview.topFinal?.team1}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      vs
                    </Typography>
                    <Typography variant="body2">
                      {knockoutPreview.topFinal?.team2}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Top Third Place */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.200" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "grey.700" }}
                >
                  3rd Place Match
                </Typography>
                <Box
                  sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {knockoutPreview.topThirdPlace?.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {knockoutPreview.topThirdPlace?.team1}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      vs
                    </Typography>
                    <Typography variant="body2">
                      {knockoutPreview.topThirdPlace?.team2}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Bottom Semifinals */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "info.light" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "info.dark" }}
                >
                  Bottom Semifinals
                </Typography>
                {knockoutPreview.bottomSemifinals?.map(
                  (match: any, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {match.description}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {getFlagEmoji(match.team1.flag)} {match.team1.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {match.team1.position}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          vs
                        </Typography>
                        <Box sx={{ flex: 1, textAlign: "right" }}>
                          <Typography variant="body2">
                            {match.team2.name} {getFlagEmoji(match.team2.flag)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {match.team2.position}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ),
                )}
              </Paper>

              {/* Bottom Final */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "secondary.light" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "secondary.dark" }}
                >
                  Bottom Final
                </Typography>
                <Box
                  sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {knockoutPreview.bottomFinal?.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {knockoutPreview.bottomFinal?.team1}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      vs
                    </Typography>
                    <Typography variant="body2">
                      {knockoutPreview.bottomFinal?.team2}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Bottom Third Place */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.200" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "grey.700" }}
                >
                  7th Place Match
                </Typography>
                <Box
                  sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {knockoutPreview.bottomThirdPlace?.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2">
                      {knockoutPreview.bottomThirdPlace?.team1}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      vs
                    </Typography>
                    <Typography variant="body2">
                      {knockoutPreview.bottomThirdPlace?.team2}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This will create 8 new matches: 4 semifinals, 2 finals, and 2
                  third-place matches. The final and third-place matches will be
                  updated automatically when semifinals are completed.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKnockoutPreviewOpen(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateKnockout}
            variant="contained"
            color="primary"
            disabled={generatingKnockout}
            startIcon={
              generatingKnockout ? <CircularProgress size={20} /> : null
            }
          >
            {generatingKnockout ? "Generating..." : "Confirm & Generate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NationsLeague;
